import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { useAtomValue } from "jotai";
import { format } from "date-fns";
import {
  Button,
  Checkbox,
  Spinner,
  Link,
  Tooltip,
} from "@fluentui/react-components";
import {
  InfoRegular,
  CheckmarkRegular,
  DismissRegular,
  VehicleCarRegular,
  MailArrowUpRegular,
  ReceiptMoneyRegular,
} from "@fluentui/react-icons";
import { toast } from "react-toastify";
import {
  useCreateOfferMutation,
  useSaveOfferMutation,
  useUpdateOfferMutation,
} from "state/queries/useOffers";
import { useCalculationMutation } from "state/queries/useCalculations";
import { transformProposalToCalculationRequest } from "utils/proposalTransformers";
import { useCascoConditions } from "state/queries/useCascoConditions";
import { useTaxonomy } from "library/utils/useTaxonomy";
import { ValidationSummary } from "components/ValidationSummary";
import InsuranceDataSection from "./sections/InsuranceDataSection";
import CascoQuotesDisplay, {
  CASCO_VARIANTS_COUNT,
} from "./components/CascoQuotesDisplay";
import { SendEmailDialog } from "components/SendEmailDialog";
import { GroupInvoiceDocuments } from "components/GroupInvoiceDocuments";
import { CreateInvoiceDialog } from "components/CreateInvoiceDialog";
import { getInvoiceEmailAddressee } from "components/InvoicePayer";
import {
  CASCO_EMAIL_TEMPLATE,
  CASCO_INVOICE_EMAIL_TEMPLATE,
  DEFAULT_SERVICE_PERSON_PHONE,
} from "library/constants/emailTemplates";
import { formatDateTimeString } from "library/utils";
import { userInfoState } from "store/index";
import { QuoteErrorBoundary } from "components/QuoteErrorBoundary";
import { QUERY_KEYS } from "library/shared";
import type { QuoteUpdateDto, ExcludedItemDto } from "library/types/types";

// Styled Components
const TabWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 17px 0 6px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FooterPanel = styled.div`
  flex-shrink: 0;
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  border-top: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  align-items: center;
`;

const SelectionSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
  margin-bottom: 12px;
`;

const SelectionPanel = styled.div`
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  padding: 22px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SelectionLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const SelectionLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.semanticColors.bodyText};
`;

const SelectionActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CheckboxList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  column-gap: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;
`;

const DocumentsSection = styled.div`
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  padding: 22px;
  margin-top: 0;
  margin-bottom: 12px;
`;

const DocumentsTitle = styled.h3`
  margin: 0 0 12px 0;
  fontsize: 14px;
  fontweight: 600;
  color: ${({ theme }) => theme.semanticColors.bodyText};
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
`;

const FileInfoBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
`;

const FileInfoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

const EmailButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const QuotesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DocumentLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${({ theme }) => theme.semanticColors.link};
  flex: 1;

  &:hover {
    color: ${({ theme }) => theme.semanticColors.linkHovered};
  }
`;

const NoDocumentsMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  border: 2px dashed ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  font-size: 14px;
`;

const EmailSentInfo = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: ${({ theme }) =>
    theme.semanticColors.cardStandoutBackground ||
    theme.semanticColors.bodyFrameBackground ||
    "#f3f2f1"};
  border-left: 3px solid
    ${({ theme }) => theme.palette.themePrimary || "#0078d4"};
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.semanticColors.bodyText};
  line-height: 1.5;
`;

// Types
interface CascoInsurer {
  id: string;
  name: string;
  displayName: string;
}

interface CascoPremium {
  year: number;
  payments: number;
  clientPremium: number;
  insurerPremium: number;
  insurerDiscount: number;
  commissionDiscount: number;
  discountDescription?: string;
  period: number;
}

interface CascoVariant {
  id: number;
  deductible: string;
  deductibleSecondAccident: string;
  deductibleTheft: string;
  deductibleTotalLoss: string;
  deductibleGlass: string;
  securitySystem?: string;
  includedRisks?: string;
  premiums: CascoPremium[];
  yearCount: number;
  hasPrices: boolean;
}

interface CascoQuote {
  insurer: string;
  coverage: string;
  premium?: number;
  discountPremium?: number;
  offerNumber?: string;
  isLoading?: boolean;
  error?: string;
  referenceNumber?: string;

  // Detailed information for variants and conditions
  variants?: CascoVariant[];
  conditions?: any;
  duration?: number;

  // Error flags
  hasErrors?: boolean;
  hasInsurerMessages?: boolean;
  hasInsurerWarnings?: boolean;
  hasInsurerErrors?: boolean;
  hasGenericErrors?: boolean;
  hasValidationErrors?: boolean;

  // Error details
  errorList?: Array<{
    code: number;
    message: string;
    description?: string | null;
  }>;
  exceptionList?: Array<{
    code: number;
    genericMessage: string;
    genericStackTrace: string;
  }>;

  // Additional metadata
  transportingExpenses?: string;
  territory?: string;
  calculator?: string;
  noteikumi?: string; // Terms and conditions reference

  // Request/Response tracking
  insurerRequest?: string[];
  insurerResponse?: string[];

  // UI state for expanded views
  selectedVariantIndex?: number;
  selectedPaymentOption?: number;
}

// Constants
const CASCO_INSURERS: CascoInsurer[] = [
  { id: "Balta", name: "Balta", displayName: "Balta" },
  { id: "Balcia", name: "Balcia", displayName: "Balcia" },
  { id: "Compensa", name: "Compensa", displayName: "Compensa" },
  { id: "BAN", name: "BAN", displayName: "BAN" },
  { id: "Ergo", name: "Ergo", displayName: "Ergo" },
  { id: "IF", name: "IF", displayName: "IF" },
  { id: "Gjensidige", name: "Gjensidige", displayName: "Gjensidige" },
  { id: "BTA", name: "BTA", displayName: "BTA" },
];

interface PaymentOption {
  id: string;
  labelKey: string;
  payments: number;
}

const PAYMENT_OPTIONS: PaymentOption[] = [
  { id: "1payment", labelKey: "qtg.cascoQuotesTab.1Payment", payments: 1 },
  { id: "2payments", labelKey: "qtg.cascoQuotesTab.2Payments", payments: 2 },
  { id: "4payments", labelKey: "qtg.cascoQuotesTab.4Payments", payments: 4 },
  { id: "12payments", labelKey: "qtg.cascoQuotesTab.12Payments", payments: 12 },
];

interface CascoQuotesTabProps {
  offerData?: any;
  onCalculationStateChange?: (isCalculating: boolean) => void;
  onOfferIdChange?: (offerId: string | null) => void;
  onOfferDataUpdate?: (updatedOffer: any) => void;
  // Shared validation props from parent
  validateRequest: (request: any) => { isValid: boolean; errors: any[] };
  validationErrors: any[];
  isValid: boolean;
  clearErrors: () => void;
  scrollToFirstError: () => void;
  getError: (fieldPath: string) => string | undefined;
  // Quotes state from parent to persist across tab switches
  quotes: CascoQuote[];
  setQuotes: React.Dispatch<React.SetStateAction<CascoQuote[]>>;
}

const CascoQuotesTab = ({
  offerData,
  onCalculationStateChange,
  onOfferIdChange,
  onOfferDataUpdate,
  validateRequest,
  validationErrors,
  isValid,
  clearErrors,
  scrollToFirstError,
  getError,
  quotes,
  setQuotes,
}: CascoQuotesTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { watch, getValues, setValue } = useFormContext();
  const taxonomy = useTaxonomy();
  const userInfo = useAtomValue(userInfoState);

  // Check if Calculate button should be hidden via environment variable
  const hideCalculateButton =
    process.env.REACT_APP_HIDE_CALCULATE_BUTTON === "true";

  // API mutation for calculation (does not create offer)
  const calculationMutation = useCalculationMutation((response) => {
    console.log("Calculation completed:", response);

    // Extract CalculationId from response
    if (response.CalculationId) {
      console.log("Got CalculationId from response:", response.CalculationId);
      // Note: We don't set offerId here since this is just a calculation
    }

    // Map the calculation response to quotes
    const calculationData = response?.Calculation?.Response?.Casco || [];
    if (Array.isArray(calculationData) && calculationData.length > 0) {
      const mappedQuotes = calculationData.map((item: any) => {
        const variants: CascoVariant[] = (item.Variants || []).map(
          (variant: any) => ({
            id: variant.ID || 0,
            deductible: variant.Deductible || "",
            deductibleSecondAccident: variant.DeductibleSecondAccident || "",
            deductibleTheft: variant.DeductibleTheft || "",
            deductibleTotalLoss: variant.DeductibleTotalLoss || "",
            deductibleGlass: variant.DeductibleGlass || "",
            securitySystem: variant.SecuritySystem,
            includedRisks: variant.IncludedRisks,
            premiums: (variant.Premiums || []).map((premium: any) => ({
              year: premium.Year || 1,
              payments: premium.Payments || 1,
              clientPremium: premium.ClientPremium || 0,
              insurerPremium: premium.InsurerPremium || 0,
              insurerDiscount: premium.InsurerDiscount || 0,
              commissionDiscount: premium.CommissionDiscount || 0,
              discountDescription: premium.DiscountDescription,
              period: premium.Period || 12,
            })),
            yearCount: variant.YearCount || 1,
            hasPrices: variant.HasPrices || false,
          }),
        );

        const errorList = (item.ErrorList || []).map((error: any) => ({
          code: error.Code || 0,
          message: error.Message || error.Description || "Unknown error",
          description: error.Description || null,
        }));

        const exceptionList = (item.ExceptionList || []).map(
          (exception: any) => ({
            code: exception.Code || 0,
            genericMessage: exception.GenericMessage || "",
            genericStackTrace: exception.GenericStackTrace || "",
          }),
        );

        return {
          insurer: item.Insurer || "Unknown",
          coverage: "comprehensive" as const,
          variants,
          hasErrors: item.HasErrors || false,
          hasInsurerMessages: item.HasInsurerMessages || false,
          hasInsurerWarnings: item.HasInsurerWarnings || false,
          hasInsurerErrors: item.HasInsurerErrors || false,
          hasGenericErrors: item.HasGenericErrors || false,
          hasValidationErrors: item.HasValidationErrors || false,
          errorList,
          exceptionList,
          duration: item.Duration || 0,
          calculator: item.Calculator || "",
          transportingExpenses: item.TransportingExpenses,
          territory: item.Territory,
          insurerRequest: item.InsurerRequest || [],
          insurerResponse: item.InsurerResponse || [],
          referenceNumber: response?.CalculationId || `${Date.now()}`,
          selectedVariantIndex: 0,
          selectedPaymentOption: variants[0]?.premiums?.[0]?.payments || 1,
        };
      });

      const normalizedQuotes = mappedQuotes.map((quote) => {
        const matchingInsurer = CASCO_INSURERS.find(
          (i) => i.id.toLowerCase() === quote.insurer.toLowerCase(),
        );
        return {
          ...quote,
          insurer: matchingInsurer ? matchingInsurer.id : quote.insurer,
        };
      });

      const filteredQuotes = normalizedQuotes.filter((quote) =>
        selectedInsurers.includes(quote.insurer),
      );

      setQuotes(filteredQuotes);

      const successCount = filteredQuotes.filter(
        (q) => q.variants && q.variants.length > 0 && !q.hasErrors,
      ).length;
      const errorCount = filteredQuotes.filter((q) => q.hasErrors).length;

      if (successCount > 0) {
        toast.success(
          t(
            "qtg.proposal.cascoQuotes.calculationSuccess",
            `Successfully calculated ${successCount} CASCO quote(s)`,
            { count: successCount },
          ),
        );
      }
      if (errorCount > 0) {
        toast.warning(
          t(
            "qtg.proposal.cascoQuotes.calculationErrors",
            `${errorCount} insurer(s) returned errors`,
            { count: errorCount },
          ),
        );
      }
    }
  });

  // API mutation for creating offer (replaces Calculations API)
  const createOfferMutation = useCreateOfferMutation((response) => {
    // Handle successful offer creation response
    console.log("Create offer completed:", response);

    // Extract OfferId from response (top-level property with PascalCase in actual response)
    if (response.OfferId) {
      console.log("Got OfferId from response:", response.OfferId);
      setValue("proposalData.offerId", response.OfferId); // Store in form state
      setValue("proposalData.proposalNumber", response.OfferId); // Also set proposal number for display
    }

    // Update Offer PDF/DOCX paths if available (nested under Offer with PascalCase)
    // These are the initial offer documents from Get Quotes operation
    if (response.Offer?.PathToOfferPdf) {
      console.log("Got offer PDF path:", response.Offer.PathToOfferPdf);
      setValue("cascoDocuments.offerPdfPath", response.Offer.PathToOfferPdf);
    }
    if (response.Offer?.PathToOfferDocx) {
      console.log("Got offer DOCX path:", response.Offer.PathToOfferDocx);
      setValue("cascoDocuments.offerDocxPath", response.Offer.PathToOfferDocx);
    }

    setSavedIncludeAdditionalConditions(
      response.Offer?.Request?.Casco?.IncludeAdditionalConditions ?? true,
    );

    // Mark that we've loaded fresh data, so we don't override it from offerData
    setHasLoadedFromOffer(true);
  });

  // Save Offer mutation
  const saveOfferMutation = useSaveOfferMutation((data) => {
    console.log("Save offer response:", data);

    // Update saved PDF/DOCX paths if available - these should replace the offer documents
    if (data?.Offer?.PathToSavedOfferPdf) {
      console.log("Got saved offer PDF path:", data.Offer.PathToSavedOfferPdf);
      setValue("cascoDocuments.savedPdfPath", data.Offer.PathToSavedOfferPdf);
      // Clear offer PDF path since we now have saved version
      setValue("cascoDocuments.offerPdfPath", null);
    }
    if (data?.Offer?.PathToSavedOfferDocx) {
      console.log(
        "Got saved offer DOCX path:",
        data.Offer.PathToSavedOfferDocx,
      );
      setValue("cascoDocuments.savedDocxPath", data.Offer.PathToSavedOfferDocx);
      // Clear offer DOCX path since we now have saved version
      setValue("cascoDocuments.offerDocxPath", null);
    }

    // Handle CascoSaved data from save response
    // The API returns CascoSaved array after saving - multiple entries per insurer for different variants
    if (
      data?.Offer?.Response?.CascoSaved &&
      data.Offer.Response.CascoSaved.length > 0
    ) {
      const cascoSavedData = data.Offer.Response.CascoSaved;
      console.log("Got CascoSaved data from save response:", cascoSavedData);

      // Group by insurer to aggregate variants and errors
      const groupedByInsurer: { [insurer: string]: any[] } = {};
      cascoSavedData.forEach((item: any) => {
        const insurerKey = item.Insurer?.toLowerCase() || "unknown";
        if (!groupedByInsurer[insurerKey]) {
          groupedByInsurer[insurerKey] = [];
        }
        groupedByInsurer[insurerKey].push(item);
      });

      // Map each insurer group to a single quote with aggregated errors
      const mappedQuotes = Object.values(groupedByInsurer).map(
        (insurerItems: any[]) => {
          const firstItem = insurerItems[0]; // Use first item as template

          // Aggregate all errors from all variants for this insurer
          const allErrors = new Set<string>();
          const allExceptions = new Set<string>();
          let hasAnyErrors = false;
          let hasAnyValidationErrors = false;

          insurerItems.forEach((item) => {
            // Collect unique error messages
            (item.ErrorList || []).forEach((error: any) => {
              const errorMsg = error.Message || error.Description;
              if (errorMsg) allErrors.add(errorMsg);
            });

            // Collect unique exception messages
            (item.ExceptionList || []).forEach((exception: any) => {
              if (exception.GenericMessage) {
                allExceptions.add(exception.GenericMessage);
              }
            });

            // Track if any variant has errors
            if (item.HasErrors) hasAnyErrors = true;
            if (item.HasValidationErrors) hasAnyValidationErrors = true;
          });

          // Create aggregated error list
          const errorList = Array.from(allErrors).map((msg, idx) => ({
            code: idx + 1,
            message: msg,
            description: null,
          }));

          // Create aggregated exception list
          const exceptionList = Array.from(allExceptions).map((msg, idx) => ({
            code: idx + 1,
            genericMessage: msg,
            genericStackTrace: "",
          }));

          // Map variants from first item (or create empty if no variants)
          const variants: CascoVariant[] = (firstItem.Variants || []).map(
            (variant: any) => ({
              id: variant.ID || 0,
              deductible: variant.Deductible || "",
              deductibleSecondAccident: variant.DeductibleSecondAccident || "",
              deductibleTheft: variant.DeductibleTheft || "",
              deductibleTotalLoss: variant.DeductibleTotalLoss || "",
              deductibleGlass: variant.DeductibleGlass || "",
              securitySystem: variant.SecuritySystem,
              includedRisks: variant.IncludedRisks,
              premiums: (variant.Premiums || []).map((premium: any) => ({
                year: premium.Year || 1,
                payments: premium.Payments || 1,
                clientPremium: premium.ClientPremium || 0,
                insurerPremium: premium.InsurerPremium || 0,
                insurerDiscount: premium.InsurerDiscount || 0,
                commissionDiscount: premium.CommissionDiscount || 0,
                discountDescription: premium.DiscountDescription,
                period: premium.Period || 12,
              })),
              yearCount: variant.YearCount || 1,
              hasPrices: variant.HasPrices || false,
            }),
          );

          return {
            insurer: firstItem.Insurer || "Unknown",
            coverage: "comprehensive" as const,
            variants,
            hasErrors:
              hasAnyErrors || errorList.length > 0 || exceptionList.length > 0,
            hasInsurerMessages: firstItem.HasInsurerMessages || false,
            hasInsurerWarnings: firstItem.HasInsurerWarnings || false,
            hasInsurerErrors: firstItem.HasInsurerErrors || false,
            hasGenericErrors: exceptionList.length > 0,
            hasValidationErrors: hasAnyValidationErrors || errorList.length > 0,
            errorList,
            exceptionList,
            duration: firstItem.Duration || 0,
            calculator: firstItem.Calculator || "",
            transportingExpenses: firstItem.TransportingExpenses,
            territory: firstItem.Territory,
            insurerRequest: firstItem.InsurerRequest || [],
            insurerResponse: firstItem.InsurerResponse || [],
            referenceNumber: offerId || `${Date.now()}`,
            selectedVariantIndex: 0,
            selectedPaymentOption: variants[0]?.premiums?.[0]?.payments || 1,
          };
        },
      );

      console.log(
        "Mapped CASCO saved quotes (grouped by insurer):",
        mappedQuotes,
      );
      setQuotes(mappedQuotes);
      // Mark as saved data so button stays disabled
      setIsSavedData(true);

      // Clear selectedQuoteForInvoice if no variants with valid prices exist
      const hasValidVariants = mappedQuotes.some(
        (quote) =>
          quote.variants &&
          quote.variants.length > 0 &&
          quote.variants.some(
            (v) => v.hasPrices && v.premiums && v.premiums.length > 0,
          ),
      );
      if (!hasValidVariants) {
        console.log(
          "No valid variants found after save - clearing selectedQuoteForInvoice",
        );
        setSelectedQuoteForInvoice(null);
      }
    }

    // Preserve CascoUpdated data from save response
    // Updates should persist after save so they remain visible
    if (data?.Offer?.Response?.CascoUpdated) {
      const updatedData = data.Offer.Response.CascoUpdated;
      console.log(
        "🔄 SAVE CALLBACK: Got CascoUpdated from save response:",
        updatedData,
      );
      console.log(
        "🔄 SAVE CALLBACK: Setting cascoUpdated state to:",
        updatedData,
      );
      setCascoUpdated(updatedData);
      setCascoSavedUpdates(updatedData);
    } else {
      console.log("⚠️ SAVE CALLBACK: No CascoUpdated in save response");
    }

    // Preserve CascoExcluded data from save response
    if (data?.Offer?.Response?.CascoExcluded) {
      const excludedData = data.Offer.Response.CascoExcluded;
      console.log(
        "🔄 SAVE CALLBACK: Got CascoExcluded from save response:",
        excludedData,
      );
      setCascoExcluded(excludedData);
      setCascoSavedExclusions(excludedData);
    }

    // Notify parent component about the updated offer data
    // This ensures the offer status and other metadata are refreshed in the UI
    if (onOfferDataUpdate && data) {
      console.log(
        "Notifying parent about updated offer data after save:",
        data,
      );
      console.log("Updated OfferStatus:", data.OfferStatus);
      onOfferDataUpdate(data);
    }

    // Navigate to the offer detail page
    if (offerId) {
      navigate(`/offer/${offerId}`);
    }
  });

  // Handler for Save Offer button
  const handleSaveOffer = () => {
    if (!offerId) {
      toast.error(
        t(
          "qtg.proposal.cascoQuotes.noOfferId",
          "No offer ID available. Please get quotes first.",
        ),
      );
      return;
    }

    saveOfferMutation.mutate(offerId);
  };

  // Fetch CASCO conditions from API
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: cascoConditions } = useCascoConditions();
  // Note: cascoConditions can be used later for displaying conditions

  // State - use form state for selections to persist across tab switches
  const selectedInsurersRaw = watch("cascoSelections.selectedInsurers");
  const selectedPaymentsRaw = watch("cascoSelections.selectedPayments");

  console.log(
    "CascoQuotesTab RENDER - selectedInsurersRaw:",
    selectedInsurersRaw,
  );
  console.log(
    "CascoQuotesTab RENDER - selectedPaymentsRaw:",
    selectedPaymentsRaw,
  );

  const selectedInsurers = useMemo(
    () => selectedInsurersRaw || [],
    [selectedInsurersRaw],
  );
  const selectedPayments = useMemo(
    () => selectedPaymentsRaw || ["1payment"],
    [selectedPaymentsRaw],
  );
  // Whether the offer document gets the additional-conditions appendix (default true)
  const includeAdditionalConditionsRaw = watch(
    "cascoSelections.includeAdditionalConditions",
  );
  const includeAdditionalConditions = includeAdditionalConditionsRaw ?? true;
  // The value stored with the offer, to tell a changed checkbox from a saved one
  const [
    savedIncludeAdditionalConditions,
    setSavedIncludeAdditionalConditions,
  ] = useState<boolean | null>(null);

  console.log(
    "CascoQuotesTab RENDER - selectedInsurers (after useMemo):",
    selectedInsurers,
  );
  console.log(
    "CascoQuotesTab RENDER - selectedPayments (after useMemo):",
    selectedPayments,
  );

  // State - quotes are now passed as props from parent
  const [isCalculating, setIsCalculating] = useState(false);
  const [hasLoadedFromOffer, setHasLoadedFromOffer] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isInvoiceEmailDialogOpen, setIsInvoiceEmailDialogOpen] =
    useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [isSavedData, setIsSavedData] = useState(false); // Track if current data is from a saved offer

  // Track pending CASCO premium updates
  const [cascoUpdated, setCascoUpdated] = useState<QuoteUpdateDto[]>([]);
  // Track saved updates (sent to backend, used to detect new changes)
  const [cascoSavedUpdates, setCascoSavedUpdates] = useState<QuoteUpdateDto[]>(
    [],
  );
  // Track excluded insurers/variants for invoice generation
  const [cascoExcluded, setCascoExcluded] = useState<ExcludedItemDto[]>([]);
  // Track saved exclusions (sent to backend, used to detect new changes)
  const [cascoSavedExclusions, setCascoSavedExclusions] = useState<
    ExcludedItemDto[]
  >([]);
  // Track original premium values before updates (for restoring when user wants to remove override)
  const [originalPremiums, setOriginalPremiums] = useState<Map<string, number>>(
    new Map(),
  );
  const [selectedQuoteForInvoice, setSelectedQuoteForInvoice] = useState<{
    insurer: string;
    variantIndex: number;
    variantID: number;
    premium: number;
    payments: number;
    period?: number;
    deductible?: string;
    deductibleSecondAccident?: string;
    deductibleTheft?: string;
    deductibleTotalLoss?: string;
    deductibleGlass?: string;
    securitySystem?: string;
    territory?: string;
    transportingExpenses?: string;
    insurerDiscount?: number;
    commissionDiscount?: number;
  } | null>(null);

  // Store document paths in form state so they persist across tab switches
  // Watch specific fields to prevent unnecessary re-renders
  const offerPdfPathRaw = watch("cascoDocuments.offerPdfPath");
  const offerDocxPathRaw = watch("cascoDocuments.offerDocxPath");
  const savedPdfPathRaw = watch("cascoDocuments.savedPdfPath");
  const savedDocxPathRaw = watch("cascoDocuments.savedDocxPath");
  const invoicePdfPathRaw = watch("cascoDocuments.invoicePdfPath");
  const invoiceDocxPathRaw = watch("cascoDocuments.invoiceDocxPath");
  const offerIdRaw = watch("proposalData.offerId");

  // Memoize to prevent re-renders when values haven't changed
  const offerPdfPath = useMemo(
    () => offerPdfPathRaw || null,
    [offerPdfPathRaw],
  );
  const offerDocxPath = useMemo(
    () => offerDocxPathRaw || null,
    [offerDocxPathRaw],
  );
  const savedPdfPath = useMemo(
    () => savedPdfPathRaw || null,
    [savedPdfPathRaw],
  );
  const savedDocxPath = useMemo(
    () => savedDocxPathRaw || null,
    [savedDocxPathRaw],
  );
  const invoicePdfPath = useMemo(
    () => invoicePdfPathRaw || null,
    [invoicePdfPathRaw],
  );
  const invoiceDocxPath = useMemo(
    () => invoiceDocxPathRaw || null,
    [invoiceDocxPathRaw],
  );
  const offerId = useMemo(() => offerIdRaw || null, [offerIdRaw]);

  // salutation and recipient of the invoice e-mail: the payer of the invoice (the client for invoices without a stored payer)
  const isClientPerson = getValues("clientData.clientType") === "person";
  const invoiceEmailAddressee = getInvoiceEmailAddressee(
    offerData?.Invoice?.Casco?.Request?.Payer,
    {
      isPerson: isClientPerson,
      name: isClientPerson
        ? `${getValues("clientData.clientFirstName") || ""} ${
            getValues("clientData.clientLastName") || ""
          }`.trim()
        : getValues("clientData.clientName") || "",
      email: getValues("clientData.clientEmail") || "",
    },
  );

  // Extract invoice data if invoice was created for Casco
  const cascoInvoiceData = useMemo(() => {
    if (offerData?.Invoice?.Casco?.Request) {
      return {
        Insurer: offerData.Invoice.Casco.Request.Insurer,
        Period: offerData.Invoice.Casco.Request.Period,
        Payments: offerData.Invoice.Casco.Request.Payments,
        VariantID: offerData.Invoice.Casco.Request.VariantID,
      };
    }
    return null;
  }, [offerData]);

  // Memoize the transformed quotes to prevent creating new objects on every render
  const transformedQuotes = useMemo(
    () =>
      quotes.map((quote) => ({
        Variants:
          quote.variants?.map((v) => ({
            ID: v.id,
            Deductible: v.deductible,
            DeductibleSecondAccident: v.deductibleSecondAccident,
            DeductibleTheft: v.deductibleTheft,
            DeductibleTotalLoss: v.deductibleTotalLoss,
            DeductibleGlass: v.deductibleGlass,
            SecuritySystem: v.securitySystem,
            IncludedRisks: v.includedRisks,
            Premiums:
              v.premiums?.map((p) => ({
                Year: p.year,
                Payments: p.payments,
                ClientPremium: p.clientPremium,
                InsurerPremium: p.insurerPremium,
                InsurerDiscount: p.insurerDiscount,
                CommissionDiscount: p.commissionDiscount,
                DiscountDescription: p.discountDescription,
                Period: p.period,
              })) || [],
            YearCount: v.yearCount,
            HasPrices: v.hasPrices,
          })) || [],
        TransportingExpenses: quote.transportingExpenses,
        Territory: quote.territory,
        HasPrices: quote.variants ? quote.variants.length > 0 : false,
        Insurer: quote.insurer,
        Calculator: quote.calculator || "",
        ErrorList:
          quote.errorList?.map((e) => ({
            Code: e.code,
            Message: e.message,
            Description: e.description || null,
          })) || [],
        Duration: quote.duration || 0,
        ExceptionList:
          quote.exceptionList?.map((e) => ({
            Code: e.code,
            GenericMessage: e.genericMessage,
            GenericStackTrace: e.genericStackTrace || "",
          })) || [],
        InsurerRequest: quote.insurerRequest || [],
        InsurerResponse: quote.insurerResponse || [],
        HasErrors: quote.hasErrors || false,
        HasInsurerMessages: quote.hasInsurerMessages || false,
        HasInsurerWarnings: quote.hasInsurerWarnings || false,
        HasInsurerErrors: quote.hasInsurerErrors || false,
        HasGenericErrors: quote.hasGenericErrors || false,
        HasValidationErrors: quote.hasValidationErrors || false,
      })),
    [quotes],
  );

  // Transform CascoSaved data from offerData to match component interface
  const transformedCascoSaved = useMemo(() => {
    const cascoSaved = offerData?.Offer?.Response?.CascoSaved;
    if (!cascoSaved || !Array.isArray(cascoSaved)) {
      return [];
    }

    return cascoSaved.map((saved: any) => ({
      Variants: [], // CascoSaved doesn't have variants, just policy info
      TransportingExpenses: saved.TransportingExpenses,
      Territory: saved.Territory,
      HasPrices: saved.HasPrices || false,
      Insurer: saved.Insurer,
      Calculator: saved.Calculator || "",
      PolicyNumber: saved.PolicyNumber,
      PremiumUnderwritten: saved.PremiumUnderwritten,
      PolicyID: saved.PolicyID,
      PolicyPeriod: saved.PolicyPeriod,
      Deductible: saved.Deductible,
      Payments: saved.Payments,
      ErrorList: saved.ErrorList || [],
      Duration: saved.Duration || 0,
      ExceptionList: saved.ExceptionList || [],
      InsurerRequest: saved.InsurerRequest || [],
      InsurerResponse: saved.InsurerResponse || [],
      HasErrors: saved.HasErrors || false,
      HasInsurerMessages: saved.HasInsurerMessages || false,
      HasInsurerWarnings: saved.HasInsurerWarnings || false,
      HasInsurerErrors: saved.HasInsurerErrors || false,
      HasGenericErrors: saved.HasGenericErrors || false,
      HasValidationErrors: saved.HasValidationErrors || false,
    }));
  }, [offerData]);

  // Extract requested insurers and payments from offer request (for display purposes)
  const requestedCascoInsurers = useMemo(() => {
    if (offerData?.Offer?.Request?.Casco?.SelectedInsurers) {
      return offerData.Offer.Request.Casco.SelectedInsurers;
    }
    return [];
  }, [offerData]);

  const requestedCascoPayments = useMemo(() => {
    if (offerData?.Offer?.Request?.Casco?.SelectedMonthsOrPayments) {
      return offerData.Offer.Request.Casco.SelectedMonthsOrPayments;
    }
    return [];
  }, [offerData]);

  // Memoized callback for quote selection to prevent infinite loops
  const handleQuoteSelectionChange = useCallback(
    (
      selection: {
        insurer: string;
        variantIndex: number;
        variantID: number;
        premium: number;
        payments: number;
        period?: number;
        deductible?: string;
        deductibleSecondAccident?: string;
        deductibleTheft?: string;
        deductibleTotalLoss?: string;
        deductibleGlass?: string;
        securitySystem?: string;
        territory?: string;
        transportingExpenses?: string;
        insurerDiscount?: number;
        commissionDiscount?: number;
      } | null,
    ) => {
      setSelectedQuoteForInvoice(selection);
    },
    [],
  );

  // Helper function to get CASCO data from offerData or fall back to quotes state
  // This is needed because offerData?.Offer?.Response?.Casco is not populated immediately after creating an offer
  const getCascoDataForInsurer = useCallback(
    (insurerName: string) => {
      // First try to get from offerData (available after page reload or refetch)
      const cascoDataFromOffer = offerData?.Offer?.Response?.Casco || [];
      const insurerFromOffer = cascoDataFromOffer.find(
        (item: any) =>
          item.Insurer?.toLowerCase() === insurerName.toLowerCase(),
      );

      if (insurerFromOffer?.Variants) {
        console.log(`📊 Using CASCO data from offerData for ${insurerName}`);
        return insurerFromOffer;
      }

      // Fall back to quotes state (available immediately after creating offer)
      const quoteFromState = quotes.find(
        (q) => q.insurer.toLowerCase() === insurerName.toLowerCase(),
      );

      if (quoteFromState?.variants) {
        console.log(
          `📊 Using CASCO data from quotes state for ${insurerName} (offerData not yet populated)`,
        );
        // Transform quotes state structure to match offerData structure (PascalCase)
        return {
          Insurer: quoteFromState.insurer,
          TransportingExpenses: quoteFromState.transportingExpenses,
          Territory: quoteFromState.territory,
          Variants: quoteFromState.variants.map((v) => ({
            ID: v.id,
            Deductible: v.deductible,
            DeductibleSecondAccident: v.deductibleSecondAccident,
            DeductibleTheft: v.deductibleTheft,
            DeductibleTotalLoss: v.deductibleTotalLoss,
            DeductibleGlass: v.deductibleGlass,
            SecuritySystem: v.securitySystem,
            IncludedRisks: v.includedRisks,
            Premiums:
              v.premiums?.map((p) => ({
                Year: p.year,
                Payments: p.payments,
                ClientPremium: p.clientPremium,
                InsurerPremium: p.insurerPremium,
                InsurerDiscount: p.insurerDiscount,
                CommissionDiscount: p.commissionDiscount,
                DiscountDescription: p.discountDescription,
                Period: p.period,
              })) || [],
            YearCount: v.yearCount,
            HasPrices: v.hasPrices,
          })),
        };
      }

      console.warn(
        `⚠️ No CASCO data found for ${insurerName} in offerData or quotes state`,
      );
      return null;
    },
    [offerData, quotes],
  );

  // Handle quote update from edit dialog - upsert into cascoUpdated array
  const handleQuoteUpdate = useCallback(
    (update: QuoteUpdateDto, originalPremium: number | null) => {
      // Validate required fields
      if (!update.Insurer) {
        console.error("Update missing Insurer:", update);
        return;
      }

      // Store original premium value if provided and premium is being updated
      if (originalPremium !== null && update.Premium !== undefined) {
        const key = `${update.Insurer}-${update.VariantID}-${update.Payments}`;
        console.log(`📝 Storing original premium for ${key}:`, originalPremium);
        setOriginalPremiums((prev) => {
          const newMap = new Map(prev);
          if (!newMap.has(key)) {
            newMap.set(key, originalPremium);
            console.log(
              `  ✅ Stored! Map now has:`,
              Array.from(newMap.entries()),
            );
          } else {
            console.log(`  ⏭️ Already stored, skipping`);
          }
          return newMap;
        });
      } else {
        console.log(
          `⚠️ NOT storing original premium - originalPremium: ${originalPremium}, update.Premium: ${update.Premium}`,
        );
      }

      setCascoUpdated((prev) => {
        // Check if variant properties (deductibles) are being updated
        // Use !== undefined to detect if field is explicitly provided (even if null)
        const hasVariantPropertyChanges =
          update.Deductible !== undefined ||
          update.DeductibleSecondAccident !== undefined ||
          update.DeductibleGlass !== undefined ||
          update.DeductibleTheft !== undefined ||
          update.DeductibleTotalLoss !== undefined;

        // Check if transporting expenses is being updated
        const hasTransportingExpensesChange =
          update.TransportingExpenses !== undefined;

        if (hasVariantPropertyChanges) {
          // Apply variant property updates to ALL payment options within this variant
          // Use ORIGINAL API data from offerData, or fall back to quotes state if not yet populated
          const originalInsurer = getCascoDataForInsurer(update.Insurer);
          const originalVariant = originalInsurer?.Variants?.find(
            (v: any) => v.ID === update.VariantID,
          );

          // Get ALL payment options from ORIGINAL API data
          const originalPayments =
            originalVariant?.Premiums?.map((p: any) => p.Payments) || [];

          // Also get any payment options from existing updates (in case custom payments were added)
          const existingPayments = prev
            .filter(
              (item) =>
                item.Insurer === update.Insurer &&
                item.VariantID === update.VariantID,
            )
            .map((item) => item.Payments);

          // Combine and deduplicate
          const allPayments = [
            ...new Set([...originalPayments, ...existingPayments]),
          ];
          const paymentOptions = allPayments.length > 0 ? allPayments : [1];

          console.log(
            `📋 Applying variant property changes to ${paymentOptions.length} payment options:`,
            paymentOptions,
            "\n  (from original API: ",
            originalPayments,
            ", from existing updates: ",
            existingPayments,
            ")",
            "\n  Variant property changes:",
            {
              Deductible: update.Deductible,
              DeductibleSecondAccident: update.DeductibleSecondAccident,
              DeductibleGlass: update.DeductibleGlass,
              DeductibleTheft: update.DeductibleTheft,
              DeductibleTotalLoss: update.DeductibleTotalLoss,
            },
          );

          // Create complete updates for all payment options with ALL fields
          const newUpdates = paymentOptions.map((payments) => {
            // Find existing update for this payment option to preserve existing values
            const existingUpdate = prev.find(
              (item) =>
                item.Insurer === update.Insurer &&
                item.VariantID === update.VariantID &&
                item.Payments === payments,
            );

            // Get original premium from API data (not quotes state)
            const originalPremiumData = originalVariant?.Premiums?.find(
              (p: any) => p.Payments === payments,
            );
            // No original premium (e.g. insurer without offer) means "not offered", not 0
            const originalPremium = originalPremiumData
              ? originalPremiumData.ClientPremium || 0
              : null;

            // Determine premium value:
            // 1. If existing update has a premium override, ALWAYS preserve it
            // 2. If this is the payment option being edited AND premium was provided (including null), use new premium
            // 3. Otherwise use original premium from API data
            const premiumValue =
              existingUpdate?.Premium !== null &&
              existingUpdate?.Premium !== undefined
                ? existingUpdate.Premium
                : payments === update.Payments && update.Premium !== undefined
                  ? update.Premium
                  : originalPremium;

            console.log(
              `  Payment ${payments}: Premium = ${premiumValue} (original: ${originalPremium}, existing: ${
                existingUpdate?.Premium ?? "none"
              }, new: ${payments === update.Payments ? update.Premium : "N/A"})`,
            );

            // Create complete QuoteUpdateDto with all fields
            return {
              Insurer: update.Insurer,
              VariantID: update.VariantID,
              Payments: payments,
              Period: originalPremiumData?.Period || 12,
              // ALWAYS include premium - can be null (not offered), number, or from original data
              Premium: premiumValue,
              // Use new variant property values for ALL payment options
              Deductible:
                update.Deductible ?? existingUpdate?.Deductible ?? null,
              DeductibleSecondAccident:
                update.DeductibleSecondAccident ??
                existingUpdate?.DeductibleSecondAccident ??
                null,
              DeductibleGlass:
                update.DeductibleGlass ??
                existingUpdate?.DeductibleGlass ??
                null,
              DeductibleTheft:
                update.DeductibleTheft ??
                existingUpdate?.DeductibleTheft ??
                null,
              DeductibleTotalLoss:
                update.DeductibleTotalLoss ??
                existingUpdate?.DeductibleTotalLoss ??
                null,
              // Preserve existing TransportingExpenses override, or keep original from API as string
              TransportingExpenses:
                existingUpdate?.TransportingExpenses ??
                (originalInsurer?.TransportingExpenses != null
                  ? String(originalInsurer.TransportingExpenses)
                  : null),
            };
          });

          // Remove existing updates for all payment options in this variant
          const filtered = prev.filter(
            (item) =>
              !(
                item.Insurer === update.Insurer &&
                item.VariantID === update.VariantID &&
                paymentOptions.includes(item.Payments)
              ),
          );

          console.log(
            `✅ Created ${newUpdates.length} updates for all payments`,
          );
          return [...filtered, ...newUpdates];
        } else if (hasTransportingExpensesChange) {
          // Apply transporting expenses update to ALL variants and payment options for this insurer
          // Use ORIGINAL API data from offerData, or fall back to quotes state if not yet populated
          const originalInsurer = getCascoDataForInsurer(update.Insurer);

          if (!originalInsurer?.Variants) {
            console.warn(
              `⚠️ Cannot apply TransportingExpenses - no variant data found for ${update.Insurer}`,
            );
            return prev;
          }

          console.log(
            `🚚 Applying TransportingExpenses changes to ALL variants and payments for ${update.Insurer}`,
          );

          // Remove existing updates for all variants/payments of this insurer
          const filtered = prev.filter(
            (item) => item.Insurer !== update.Insurer,
          );

          // Create updates for all variants and payment options
          // Same variants as shown in the table: original ones, ones with manual updates,
          // and placeholders up to CASCO_VARIANTS_COUNT (e.g. insurer without an offer)
          const variantIds: number[] = [
            ...new Set<number>([
              ...originalInsurer.Variants.map((v: any) => v.ID),
              ...prev
                .filter((item) => item.Insurer === update.Insurer)
                .map((item) => item.VariantID),
            ]),
          ];
          for (let id = 1; variantIds.length < CASCO_VARIANTS_COUNT; id++) {
            if (!variantIds.includes(id)) variantIds.push(id);
          }
          const variants = variantIds.map(
            (id) =>
              originalInsurer.Variants.find((v: any) => v.ID === id) ?? {
                ID: id,
                Premiums: [],
              },
          );
          const newUpdates: QuoteUpdateDto[] = [];
          variants.forEach((variant: any) => {
            // Include payment options from existing updates so manually added ones aren't dropped
            const existingPayments = prev
              .filter(
                (item) =>
                  item.Insurer === update.Insurer &&
                  item.VariantID === variant.ID,
              )
              .map((item) => item.Payments);
            const allPayments: number[] = [
              ...new Set<number>([
                ...(variant.Premiums?.map((p: any) => p.Payments) ?? []),
                ...existingPayments,
              ]),
            ];
            const paymentOptions = allPayments.length > 0 ? allPayments : [1];

            paymentOptions.forEach((payments: number) => {
              // Find existing update to preserve premium and deductibles
              const existingUpdate = prev.find(
                (item) =>
                  item.Insurer === update.Insurer &&
                  item.VariantID === variant.ID &&
                  item.Payments === payments,
              );

              // Get original premium from API data
              const originalPremiumData = variant.Premiums?.find(
                (p: any) => p.Payments === payments,
              );
              const originalPremium = originalPremiumData
                ? originalPremiumData.ClientPremium || 0
                : null;

              // Use existing premium override if it exists (including null), otherwise use original
              const premiumValue =
                existingUpdate?.Premium !== undefined
                  ? existingUpdate.Premium
                  : originalPremium;

              newUpdates.push({
                Insurer: update.Insurer,
                VariantID: variant.ID,
                Payments: payments,
                Period: originalPremiumData?.Period || 12,
                // TransportingExpenses is string | null
                TransportingExpenses: update.TransportingExpenses ?? null,
                // ALWAYS include premium - can be null (not offered), number, or from original data
                Premium: premiumValue,
                // Preserve existing overrides OR original variant values
                Deductible:
                  existingUpdate?.Deductible ?? variant.Deductible ?? null,
                DeductibleSecondAccident:
                  existingUpdate?.DeductibleSecondAccident ??
                  variant.DeductibleSecondAccident ??
                  null,
                DeductibleGlass:
                  existingUpdate?.DeductibleGlass ??
                  variant.DeductibleGlass ??
                  null,
                DeductibleTheft:
                  existingUpdate?.DeductibleTheft ??
                  variant.DeductibleTheft ??
                  null,
                DeductibleTotalLoss:
                  existingUpdate?.DeductibleTotalLoss ??
                  variant.DeductibleTotalLoss ??
                  null,
              });
            });
          });

          console.log(
            `✅ Created ${newUpdates.length} updates for all variants/payments`,
          );
          return [...filtered, ...newUpdates];
        } else {
          // Standard premium update - single payment option
          // Remove existing update for same insurer/period/payments/variantID
          const filtered = prev.filter(
            (item) =>
              !(
                item.Insurer === update.Insurer &&
                item.Period === update.Period &&
                item.Payments === update.Payments &&
                item.VariantID === update.VariantID
              ),
          );

          // If premium matches original AND is not null, this is a "remove override" - don't add it back
          // But if premium is null, always add it (user explicitly wants to mark as "not offered")
          if (update.Premium !== null && update.Premium === originalPremium) {
            return filtered;
          }

          // Find existing update to preserve any existing deductible/transportingExpenses overrides
          const existingUpdate = prev.find(
            (item) =>
              item.Insurer === update.Insurer &&
              item.Period === update.Period &&
              item.Payments === update.Payments &&
              item.VariantID === update.VariantID,
          );

          // Get original variant data to fill in required deductible fields
          const originalInsurer = getCascoDataForInsurer(update.Insurer);
          const originalVariant = originalInsurer?.Variants?.find(
            (v: any) => v.ID === update.VariantID,
          );

          console.log(
            `🔍 Standard premium update - getting original data for ${update.Insurer}:`,
            {
              hasOriginalInsurer: !!originalInsurer,
              hasOriginalVariant: !!originalVariant,
              originalTE: originalInsurer?.TransportingExpenses,
              originalDeductible: originalVariant?.Deductible,
              existingUpdate: existingUpdate,
            },
          );

          // Helper to get TransportingExpenses as string (can be string or number from API)
          // Returns undefined if there's no value to preserve (don't include in update)
          // Returns string if there's an existing override or original value to preserve
          const getTransportingExpenses = (): string | null | undefined => {
            // First check existing update
            if (existingUpdate?.TransportingExpenses !== undefined) {
              return existingUpdate.TransportingExpenses;
            }
            // Then try original insurer data
            const originalTE = originalInsurer?.TransportingExpenses;
            if (
              originalTE !== undefined &&
              originalTE !== null &&
              originalTE !== ""
            ) {
              return String(originalTE);
            }
            // Return undefined to signal "don't include in update" - no value to preserve
            return undefined;
          };

          // Get TransportingExpenses value (undefined means no original value)
          const teValue = getTransportingExpenses();

          // Create complete update with all required fields
          // Priority: existing override > original value from API > null
          const completeUpdate: QuoteUpdateDto = {
            Insurer: update.Insurer,
            VariantID: update.VariantID,
            Payments: update.Payments,
            Period: update.Period,
            Premium: update.Premium,
            // Preserve existing overrides or use original values from API
            Deductible:
              existingUpdate?.Deductible ?? originalVariant?.Deductible ?? null,
            DeductibleSecondAccident:
              existingUpdate?.DeductibleSecondAccident ??
              originalVariant?.DeductibleSecondAccident ??
              null,
            DeductibleGlass:
              existingUpdate?.DeductibleGlass ??
              originalVariant?.DeductibleGlass ??
              null,
            DeductibleTheft:
              existingUpdate?.DeductibleTheft ??
              originalVariant?.DeductibleTheft ??
              null,
            DeductibleTotalLoss:
              existingUpdate?.DeductibleTotalLoss ??
              originalVariant?.DeductibleTotalLoss ??
              null,
            // TransportingExpenses: use existing override, original value, or null
            TransportingExpenses: teValue ?? null,
          };

          console.log(
            `📝 Standard premium update for ${update.Insurer} V${update.VariantID} P${update.Payments}:`,
            {
              premium: completeUpdate.Premium,
              deductible: completeUpdate.Deductible,
              transportingExpenses: completeUpdate.TransportingExpenses,
              teIncluded: teValue !== undefined,
              originalTE: originalInsurer?.TransportingExpenses,
              existingTE: existingUpdate?.TransportingExpenses,
            },
          );

          // Add complete update
          return [...filtered, completeUpdate];
        }
      });
    },
    [getCascoDataForInsurer],
  );

  // Handle reset of deductibles to original API values - removes variant field overrides
  const handleResetDeductibles = useCallback(
    (insurer: string, variantID: number) => {
      console.log(
        `🔄 Resetting deductibles for ${insurer} variant ${variantID}`,
      );

      // Get original variant data from API
      const originalInsurer = getCascoDataForInsurer(insurer);
      // Insurer without an offer has no original variant - reset to empty values
      const originalVariant = originalInsurer?.Variants?.find(
        (v: any) => v.ID === variantID,
      ) ?? {
        Premiums: [],
        Deductible: null,
        DeductibleSecondAccident: null,
        DeductibleGlass: null,
        DeductibleTheft: null,
        DeductibleTotalLoss: null,
      };

      setCascoUpdated((prev) => {
        // Find all updates for this insurer/variant
        const variantUpdates = prev.filter(
          (item) =>
            item.Insurer.toLowerCase() === insurer.toLowerCase() &&
            item.VariantID === variantID,
        );

        // If there are no updates for this variant, nothing to reset
        if (variantUpdates.length === 0) {
          console.log(`  No updates found for this variant, nothing to reset`);
          return prev;
        }

        // Check if any update has only deductible changes (no premium override)
        // If so, we can remove those updates entirely
        // If update has premium override, we need to keep it but reset deductibles to original

        const updatesToKeep: QuoteUpdateDto[] = [];
        const updatesToRemove: QuoteUpdateDto[] = [];

        variantUpdates.forEach((update) => {
          // Get original premium for this payment option
          const originalPremiumData = originalVariant.Premiums?.find(
            (p: any) => p.Payments === update.Payments,
          );
          const originalPremium = originalPremiumData?.ClientPremium ?? null;

          // Check if this update has a premium override (different from original)
          const hasPremiumOverride =
            update.Premium !== undefined && update.Premium !== originalPremium;

          // Check if deductibles differ from original
          const hasDeductibleChanges =
            update.Deductible !== originalVariant.Deductible ||
            update.DeductibleSecondAccident !==
              originalVariant.DeductibleSecondAccident ||
            update.DeductibleGlass !== originalVariant.DeductibleGlass ||
            update.DeductibleTheft !== originalVariant.DeductibleTheft ||
            update.DeductibleTotalLoss !== originalVariant.DeductibleTotalLoss;

          if (hasPremiumOverride) {
            // Keep the update but reset deductibles to original values
            updatesToKeep.push({
              ...update,
              Deductible: originalVariant.Deductible ?? null,
              DeductibleSecondAccident:
                originalVariant.DeductibleSecondAccident ?? null,
              DeductibleGlass: originalVariant.DeductibleGlass ?? null,
              DeductibleTheft: originalVariant.DeductibleTheft ?? null,
              DeductibleTotalLoss: originalVariant.DeductibleTotalLoss ?? null,
            });
          } else if (hasDeductibleChanges) {
            // No premium override and deductibles were changed - can remove entirely
            updatesToRemove.push(update);
          } else {
            // No changes at all, keep as is (shouldn't normally happen)
            updatesToKeep.push(update);
          }
        });

        console.log(
          `  Keeping ${updatesToKeep.length} updates with premium overrides, removing ${updatesToRemove.length}`,
        );

        // Remove variant updates and add back only those we need to keep
        const filtered = prev.filter(
          (item) =>
            !(
              item.Insurer.toLowerCase() === insurer.toLowerCase() &&
              item.VariantID === variantID
            ),
        );

        return [...filtered, ...updatesToKeep];
      });
    },
    [getCascoDataForInsurer],
  );

  // Handle exclusion change from CascoQuotesDisplay
  const handleExclusionChange = useCallback((exclusions: ExcludedItemDto[]) => {
    console.log("📋 Exclusion change:", exclusions);
    setCascoExcluded(exclusions);
  }, []);

  // Initialize update mutation for quote premium edits (only when offerId is available)
  const updateOfferMutation = useUpdateOfferMutation(
    offerId || "",
    (response) => {
      console.log("Update offer completed:", response);

      // Mark current updates as saved (don't clear, keep all updates)
      setCascoSavedUpdates([...cascoUpdated]);
      // Mark current exclusions as saved
      setCascoSavedExclusions([...cascoExcluded]);
      setSavedIncludeAdditionalConditions(
        response.Offer?.Request?.Casco?.IncludeAdditionalConditions ?? true,
      );

      // Invalidate and refetch offer data
      if (offerId) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.offers, offerId],
        });
      }

      toast.success(
        t("qtg.proposal.updateSuccess", "Quote updates saved successfully"),
      );
    },
  );

  // Check if there are NEW pending changes (not yet saved)
  const hasNewPendingChanges = useMemo(() => {
    // Check the additional-conditions choice
    if (
      savedIncludeAdditionalConditions !== null &&
      includeAdditionalConditions !== savedIncludeAdditionalConditions
    )
      return true;

    // Check quote updates
    if (cascoUpdated.length !== cascoSavedUpdates.length) return true;

    // Check if any update is different from saved
    const hasQuoteChanges = cascoUpdated.some((update) => {
      const saved = cascoSavedUpdates.find(
        (s) =>
          s.Insurer === update.Insurer &&
          s.Period === update.Period &&
          s.Payments === update.Payments &&
          s.VariantID === update.VariantID,
      );

      if (!saved) return true;

      // Compare all fields that could have changed
      return (
        saved.Premium !== update.Premium ||
        saved.Deductible !== update.Deductible ||
        saved.DeductibleSecondAccident !== update.DeductibleSecondAccident ||
        saved.DeductibleGlass !== update.DeductibleGlass ||
        saved.DeductibleTheft !== update.DeductibleTheft ||
        saved.DeductibleTotalLoss !== update.DeductibleTotalLoss ||
        saved.TransportingExpenses !== update.TransportingExpenses
      );
    });

    if (hasQuoteChanges) return true;

    // Check exclusion changes
    if (cascoExcluded.length !== cascoSavedExclusions.length) return true;

    const hasExclusionChanges = cascoExcluded.some((exclusion) => {
      const saved = cascoSavedExclusions.find(
        (s) =>
          s.Insurer === exclusion.Insurer &&
          // Treat null and undefined as equivalent for VariantID comparison
          (s.VariantID ?? null) === (exclusion.VariantID ?? null),
      );
      return !saved;
    });

    return hasExclusionChanges;
  }, [
    cascoUpdated,
    cascoSavedUpdates,
    cascoExcluded,
    cascoSavedExclusions,
    includeAdditionalConditions,
    savedIncludeAdditionalConditions,
  ]);

  // Handle Update Offer button click - send all pending updates to backend
  const handleUpdateOffer = useCallback(() => {
    if (!offerId) {
      toast.error(t("qtg.proposal.noOfferId", "No offer ID available"));
      return;
    }

    // if (cascoUpdated.length === 0) {
    //   toast.warning(t("qtg.proposal.noUpdates", "No updates to save"));
    //   return;
    // }

    // Send update with cascoUpdated and cascoExcluded arrays
    updateOfferMutation.mutate({
      CascoUpdated: cascoUpdated,
      CascoExcluded: cascoExcluded,
      CascoIncludeAdditionalConditions: includeAdditionalConditions,
    });
  }, [
    offerId,
    cascoUpdated,
    cascoExcluded,
    includeAdditionalConditions,
    updateOfferMutation,
    t,
  ]);

  // Notify parent when offerId changes (for backward compatibility)
  useEffect(() => {
    if (onOfferIdChange) {
      onOfferIdChange(offerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  // Reset hasLoadedFromOffer when offerData.OfferId changes (navigation to different offer)
  // This ensures documents and quotes load correctly after navigation
  useEffect(() => {
    if (offerData?.OfferId && offerData.OfferId !== offerId) {
      console.log(
        "OfferId mismatch detected - resetting hasLoadedFromOffer flag",
        { offerDataId: offerData.OfferId, currentOfferId: offerId },
      );
      setHasLoadedFromOffer(false);
    }
  }, [offerData?.OfferId, offerId]);

  // Debug: Log when component mounts/unmounts
  useEffect(() => {
    console.log("=== CascoQuotesTab MOUNTED ===");
    return () => {
      console.log("=== CascoQuotesTab UNMOUNTED ===");
    };
  }, []);

  // Initialize form values if they don't exist
  useEffect(() => {
    const currentInsurers = getValues("cascoSelections.selectedInsurers");
    const currentPayments = getValues("cascoSelections.selectedPayments");

    console.log("CascoQuotesTab INIT - currentInsurers:", currentInsurers);
    console.log("CascoQuotesTab INIT - currentPayments:", currentPayments);

    if (!currentInsurers || currentInsurers.length === 0) {
      const allInsurers = CASCO_INSURERS.filter(
        (i) => i.id !== "Gjensidige",
      ).map((i) => i.id);
      console.log("Initializing selectedInsurers to all:", allInsurers);
      setValue("cascoSelections.selectedInsurers", allInsurers);
    }
    if (!currentPayments || currentPayments.length === 0) {
      const allPayments = PAYMENT_OPTIONS.map((p) => p.id);
      console.log("Initializing selectedPayments to all:", allPayments);
      setValue("cascoSelections.selectedPayments", allPayments);
    }
  }, [getValues, setValue]);

  // Notify parent component when calculation state changes
  useEffect(() => {
    const calculating = createOfferMutation.isPending || isCalculating;
    if (onCalculationStateChange) {
      onCalculationStateChange(calculating);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOfferMutation.isPending, isCalculating]);

  // Load quotes from offerData when component mounts or offerData changes
  useEffect(() => {
    console.log("=== CascoQuotesTab useEffect - offerData changed ===", {
      hasOfferData: !!offerData,
      offerId: offerData?.OfferId,
      hasPdfPath: !!offerData?.Offer?.PathToOfferPdf,
      hasDocxPath: !!offerData?.Offer?.PathToOfferDocx,
      currentFormOfferId: offerId,
      currentOfferPdfPath: offerPdfPath,
      currentOfferDocxPath: offerDocxPath,
    });

    // Always extract OfferId and document paths when offerData is available, regardless of quotes
    if (offerData?.OfferId) {
      console.log("Loading OfferId from offerData:", offerData.OfferId);
      setValue("proposalData.offerId", offerData.OfferId); // Store in form state
    }

    // Restore the additional-conditions choice stored with the offer
    if (offerData?.Offer?.Request?.Casco) {
      const stored =
        offerData.Offer.Request.Casco.IncludeAdditionalConditions ?? true;
      setValue("cascoSelections.includeAdditionalConditions", stored);
      setSavedIncludeAdditionalConditions(stored);
    }

    // Extract document paths - prioritize saved documents over offer documents
    // IMPORTANT: Only update document paths if they exist in offerData
    // Don't clear existing paths if offerData doesn't have them (prevents clearing after navigation)
    if (offerData?.Offer?.PathToSavedOfferPdf) {
      console.log(
        "Loading saved offer PDF path from offerData:",
        offerData.Offer.PathToSavedOfferPdf,
      );
      setValue(
        "cascoDocuments.savedPdfPath",
        offerData.Offer.PathToSavedOfferPdf,
      );
    } else if (offerData?.Offer?.PathToOfferPdf) {
      console.log(
        "Loading offer PDF path from offerData:",
        offerData.Offer.PathToOfferPdf,
      );
      setValue("cascoDocuments.offerPdfPath", offerData.Offer.PathToOfferPdf);
    }

    if (offerData?.Offer?.PathToSavedOfferDocx) {
      console.log(
        "Loading saved offer DOCX path from offerData:",
        offerData.Offer.PathToSavedOfferDocx,
      );
      setValue(
        "cascoDocuments.savedDocxPath",
        offerData.Offer.PathToSavedOfferDocx,
      );
    } else if (offerData?.Offer?.PathToOfferDocx) {
      console.log(
        "Loading offer DOCX path from offerData:",
        offerData.Offer.PathToOfferDocx,
      );
      setValue("cascoDocuments.offerDocxPath", offerData.Offer.PathToOfferDocx);
    }

    // Extract invoice documents if available
    if (offerData?.Invoice?.Casco?.PathToInvoicePdf) {
      console.log(
        "Loading invoice PDF path from offerData:",
        offerData.Invoice.Casco.PathToInvoicePdf,
      );
      setValue(
        "cascoDocuments.invoicePdfPath",
        offerData.Invoice.Casco.PathToInvoicePdf,
      );
    }

    if (offerData?.Invoice?.Casco?.PathToInvoiceDocx) {
      console.log(
        "Loading invoice DOCX path from offerData:",
        offerData.Invoice.Casco.PathToInvoiceDocx,
      );
      setValue(
        "cascoDocuments.invoiceDocxPath",
        offerData.Invoice.Casco.PathToInvoiceDocx,
      );
    }

    // Load CascoUpdated data from backend if available
    // IMPORTANT: This must be BEFORE early return guards so updates are always reloaded
    if (offerData?.Offer?.Response?.CascoUpdated) {
      const updatedData = offerData.Offer.Response.CascoUpdated;
      console.log(
        "📥 USEEFFECT: Loading CASCO updates from offerData:",
        updatedData,
      );
      console.log("📥 USEEFFECT: Setting cascoUpdated state to:", updatedData);
      // Store in local state to display updated values
      setCascoUpdated(updatedData);
      // Mark these as saved (so we know there are no NEW pending changes)
      setCascoSavedUpdates(updatedData);
    } else {
      console.log("⚠️ USEEFFECT: No CascoUpdated in offerData");
    }

    // Load CascoExcluded data from backend if available
    if (offerData?.Offer?.Response?.CascoExcluded) {
      const excludedData = offerData.Offer.Response.CascoExcluded;
      console.log(
        "📥 USEEFFECT: Loading CASCO exclusions from offerData:",
        excludedData,
      );
      setCascoExcluded(excludedData);
      setCascoSavedExclusions(excludedData);
    }

    // Skip loading quotes if we've already loaded fresh offer data
    if (hasLoadedFromOffer) {
      console.log("Skipping quotes load - already have fresh offer data");
      return;
    }

    // Skip loading quotes if quotes already exist (preserve calculated quotes)
    if (quotes.length > 0) {
      console.log("Skipping quotes load - quotes already exist");
      return;
    }

    // Get CASCO data from structure: Offer.Response.Casco
    // Business rule: ALWAYS use Casco for quote data (contains variants with premiums)
    // CascoSaved is passed separately and merged in CascoQuotesDisplay for saved policy info
    const cascoData = offerData?.Offer?.Response?.Casco;

    if (cascoData) {
      console.log("Loading CASCO quotes from offer:", cascoData);

      if (Array.isArray(cascoData) && cascoData.length > 0) {
        // Group by insurer to aggregate variants and errors (same logic as save response handler)
        const groupedByInsurer: { [insurer: string]: any[] } = {};
        cascoData.forEach((item: any) => {
          const insurerKey = item.Insurer?.toLowerCase() || "unknown";
          if (!groupedByInsurer[insurerKey]) {
            groupedByInsurer[insurerKey] = [];
          }
          groupedByInsurer[insurerKey].push(item);
        });

        // Map each insurer group to a single quote with aggregated errors
        const mappedQuotes = Object.values(groupedByInsurer).map(
          (insurerItems: any[]) => {
            const firstItem = insurerItems[0]; // Use first item as template

            // Normalize insurer name (case-insensitive matching)
            const matchingInsurer = CASCO_INSURERS.find(
              (i) => i.id.toLowerCase() === firstItem.Insurer.toLowerCase(),
            );
            const normalizedInsurer = matchingInsurer
              ? matchingInsurer.id
              : firstItem.Insurer;

            // Aggregate all errors from all variants for this insurer
            const allErrors = new Set<string>();
            const allExceptions = new Set<string>();
            let hasAnyErrors = false;
            let hasAnyValidationErrors = false;

            insurerItems.forEach((item) => {
              // Collect unique error messages
              (item.ErrorList || []).forEach((error: any) => {
                const errorMsg = error.Message || error.Description;
                if (errorMsg) allErrors.add(errorMsg);
              });

              // Collect unique exception messages
              (item.ExceptionList || []).forEach((exception: any) => {
                if (exception.GenericMessage) {
                  allExceptions.add(exception.GenericMessage);
                }
              });

              // Track if any variant has errors
              if (item.HasErrors) hasAnyErrors = true;
              if (item.HasValidationErrors) hasAnyValidationErrors = true;
            });

            // Create aggregated error list
            const errorList = Array.from(allErrors).map((msg, idx) => ({
              code: idx + 1,
              message: msg,
              description: null,
            }));

            // Create aggregated exception list
            const exceptionList = Array.from(allExceptions).map((msg, idx) => ({
              code: idx + 1,
              genericMessage: msg,
              genericStackTrace: "",
            }));

            // Map variants from first item (or create empty if no variants)
            const variants: CascoVariant[] = (firstItem.Variants || []).map(
              (variant: any) => ({
                id: variant.ID || 0,
                deductible: variant.Deductible || "",
                deductibleSecondAccident:
                  variant.DeductibleSecondAccident || "",
                deductibleTheft: variant.DeductibleTheft || "",
                deductibleTotalLoss: variant.DeductibleTotalLoss || "",
                deductibleGlass: variant.DeductibleGlass || "",
                securitySystem: variant.SecuritySystem,
                includedRisks: variant.IncludedRisks,
                premiums: (variant.Premiums || []).map((premium: any) => ({
                  year: premium.Year || 1,
                  payments: premium.Payments || 1,
                  clientPremium: premium.ClientPremium || 0,
                  insurerPremium: premium.InsurerPremium || 0,
                  insurerDiscount: premium.InsurerDiscount || 0,
                  commissionDiscount: premium.CommissionDiscount || 0,
                  discountDescription: premium.DiscountDescription,
                  period: premium.Period || 12,
                })),
                yearCount: variant.YearCount || 1,
                hasPrices: variant.HasPrices || false,
              }),
            );

            return {
              insurer: normalizedInsurer,
              coverage: "comprehensive" as const,
              variants,
              hasErrors:
                hasAnyErrors ||
                errorList.length > 0 ||
                exceptionList.length > 0,
              hasInsurerMessages: firstItem.HasInsurerMessages || false,
              hasInsurerWarnings: firstItem.HasInsurerWarnings || false,
              hasInsurerErrors: firstItem.HasInsurerErrors || false,
              hasGenericErrors: exceptionList.length > 0,
              hasValidationErrors:
                hasAnyValidationErrors || errorList.length > 0,
              errorList,
              exceptionList,
              duration: firstItem.Duration || 0,
              calculator: firstItem.Calculator || "",
              transportingExpenses: firstItem.TransportingExpenses,
              territory: firstItem.Territory,
              insurerRequest: firstItem.InsurerRequest || [],
              insurerResponse: firstItem.InsurerResponse || [],
              referenceNumber: offerId || `${Date.now()}`,
              selectedVariantIndex: 0,
              selectedPaymentOption: variants[0]?.premiums?.[0]?.payments || 1,
            };
          },
        );

        console.log("Mapped and normalized CASCO quotes:", mappedQuotes);
        setQuotes(mappedQuotes);

        // Pre-select insurers that have quotes with data (variants and premiums)
        // Show quotes even if they have errors - error status should not hide available data
        const insurersWithQuotes = mappedQuotes
          .filter(
            (q) =>
              q.variants &&
              q.variants.length > 0 &&
              q.variants.some((v) => v.premiums && v.premiums.length > 0),
          )
          .map((q) => q.insurer);
        console.log("Auto-selecting CASCO insurers:", insurersWithQuotes);
        setValue("cascoSelections.selectedInsurers", insurersWithQuotes);
      }

      setHasLoadedFromOffer(true);
    }
  }, [
    offerData,
    hasLoadedFromOffer,
    setValue,
    setQuotes,
    quotes.length,
    offerId,
    offerPdfPath,
    offerDocxPath,
  ]);

  // Watch form data for validation
  const vehicleData = watch("vehicleData");
  const clientData = watch("clientData");

  // Check if form has required data for quotes
  const canCalculateQuotes =
    vehicleData?.licensePlateNumber &&
    vehicleData?.vehicleMake &&
    vehicleData?.vehicleModel &&
    clientData?.clientCode;

  const handleInsurerToggle = (insurerId: string) => {
    const current = selectedInsurers;
    const newValue = current.includes(insurerId)
      ? current.filter((id) => id !== insurerId)
      : [...current, insurerId];
    console.log("Setting selectedInsurers to:", newValue);
    setValue("cascoSelections.selectedInsurers", newValue);
  };

  const handlePaymentToggle = (paymentId: string) => {
    const current = selectedPayments;
    const newValue = current.includes(paymentId)
      ? current.filter((id) => id !== paymentId)
      : [...current, paymentId];
    console.log("Setting selectedPayments to:", newValue);
    setValue("cascoSelections.selectedPayments", newValue);
  };

  const handleGetQuotes = async () => {
    // Clear previous validation errors
    clearErrors();

    if (!canCalculateQuotes || selectedInsurers.length === 0) {
      toast.warning(
        t(
          "qtg.cascoQuotesTab.pleaseSelectAtLeastOneInsurerAndEnsureAllRequiredFieldsAreFilled",
        ),
      );
      return;
    }

    if (selectedPayments.length === 0) {
      toast.warning(
        t("qtg.cascoQuotesTab.pleaseSelectAtLeastOnePaymentOption"),
      );
      return;
    }

    // Get current form data
    const formData = getValues();

    // Convert selected payment IDs to payment numbers
    const selectedPaymentNumbers = selectedPayments.map((paymentId) => {
      const payment = PAYMENT_OPTIONS.find((p) => p.id === paymentId);
      return payment ? payment.payments : 1; // Default to 1 if not found
    });
    console.log("Selected payment options:", selectedPaymentNumbers);

    // Transform form data to calculation request with selected insurers (lowercase) and payments
    const calculationRequest = transformProposalToCalculationRequest(
      formData,
      taxonomy,
      selectedInsurers.map((insurer) => insurer.toLowerCase()),
      ["casco"], // Explicitly request CASCO quotes
      selectedPaymentNumbers, // Pass selected payment options
      userInfo,
    );

    // Validate the calculation request BEFORE setting calculating state
    const validation = validateRequest(calculationRequest);
    if (!validation.isValid) {
      const errorCount = validation.errors.filter(
        (e) => e.severity === "error",
      ).length;
      toast.error(
        `Please correct ${errorCount} validation error${
          errorCount !== 1 ? "s" : ""
        } before calculating`,
        { autoClose: 2000 }, // Reduced time since validation errors are displayed inline
      );
      // Don't call scrollToFirstError here - the error fields are in General Info tab
      // User can see the errors when they switch tabs
      return; // Return early without setting calculating state
    }

    // Only set calculating state after validation passes
    setIsCalculating(true);
    // Don't set quotes yet - wait for the response to come back
    // This keeps the selection panel visible during loading

    try {
      // Call the Offers API (replaces Calculations API)
      const response =
        await createOfferMutation.mutateAsync(calculationRequest);

      console.log("CASCO offer response:", response);

      // Extract OfferId from response and store in form state
      if (response?.OfferId) {
        console.log("Got OfferId from response:", response.OfferId);
        setValue("proposalData.offerId", response.OfferId);

        // Navigate to the offer URL while staying on the proposal tab
        navigate(`/offer/${response.OfferId}`, { replace: true });
      }

      // Update PDF/DOCX paths if available
      if (response?.Offer?.PathToOfferPdf) {
        console.log("Got offer PDF path:", response.Offer.PathToOfferPdf);
        setValue("cascoDocuments.offerPdfPath", response.Offer.PathToOfferPdf);
      }
      if (response?.Offer?.PathToOfferDocx) {
        console.log("Got offer DOCX path:", response.Offer.PathToOfferDocx);
        setValue(
          "cascoDocuments.offerDocxPath",
          response.Offer.PathToOfferDocx,
        );
      }

      // Extract CASCO data from the new response structure
      // Note: Top-level is camelCase (offer), but nested properties are PascalCase (Offer.Response.Casco)
      const cascoData = response?.Offer?.Response?.Casco || [];

      console.log("CASCO data from response:", cascoData);

      // Map the CASCO response to quotes
      if (Array.isArray(cascoData) && cascoData.length > 0) {
        const mappedQuotes = cascoData.map((item: any) => {
          // Map variants with all deductible options
          const variants: CascoVariant[] = (item.Variants || []).map(
            (variant: any) => ({
              id: variant.ID || 0,
              deductible: variant.Deductible || "",
              deductibleSecondAccident: variant.DeductibleSecondAccident || "",
              deductibleTheft: variant.DeductibleTheft || "",
              deductibleTotalLoss: variant.DeductibleTotalLoss || "",
              deductibleGlass: variant.DeductibleGlass || "",
              securitySystem: variant.SecuritySystem,
              includedRisks: variant.IncludedRisks,
              premiums: (variant.Premiums || []).map((premium: any) => ({
                year: premium.Year || 1,
                payments: premium.Payments || 1,
                clientPremium: premium.ClientPremium || 0,
                insurerPremium: premium.InsurerPremium || 0,
                insurerDiscount: premium.InsurerDiscount || 0,
                commissionDiscount: premium.CommissionDiscount || 0,
                discountDescription: premium.DiscountDescription,
                period: premium.Period || 12,
              })),
              yearCount: variant.YearCount || 1,
              hasPrices: variant.HasPrices || false,
            }),
          );

          // Map error list
          const errorList = (item.ErrorList || []).map((error: any) => ({
            code: error.Code || 0,
            message: error.Message || error.Description || "Unknown error",
            description: error.Description || null,
          }));

          // Map exception list
          const exceptionList = (item.ExceptionList || []).map(
            (exception: any) => ({
              code: exception.Code || 0,
              genericMessage: exception.GenericMessage || "",
              genericStackTrace: exception.GenericStackTrace || "",
            }),
          );

          return {
            insurer: item.Insurer || "Unknown",
            coverage: "comprehensive" as const,
            variants,
            hasErrors: item.HasErrors || false,
            hasInsurerMessages: item.HasInsurerMessages || false,
            hasInsurerWarnings: item.HasInsurerWarnings || false,
            hasInsurerErrors: item.HasInsurerErrors || false,
            hasGenericErrors: item.HasGenericErrors || false,
            hasValidationErrors: item.HasValidationErrors || false,
            errorList,
            exceptionList,
            duration: item.Duration || 0,
            calculator: item.Calculator || "",
            transportingExpenses: item.TransportingExpenses,
            territory: item.Territory,
            insurerRequest: item.InsurerRequest || [],
            insurerResponse: item.InsurerResponse || [],
            referenceNumber: response?.OfferId || `${Date.now()}`,
            selectedVariantIndex: 0,
            selectedPaymentOption: variants[0]?.premiums?.[0]?.payments || 1,
          };
        });

        console.log("Mapped CASCO quotes:", mappedQuotes);

        // Normalize insurer names and filter
        const normalizedQuotes = mappedQuotes.map((quote) => {
          const matchingInsurer = CASCO_INSURERS.find(
            (i) => i.id.toLowerCase() === quote.insurer.toLowerCase(),
          );
          return {
            ...quote,
            insurer: matchingInsurer ? matchingInsurer.id : quote.insurer,
          };
        });

        // Only keep insurers that user selected
        const filteredQuotes = normalizedQuotes.filter((quote) =>
          selectedInsurers.includes(quote.insurer),
        );

        console.log("Filtered quotes with defaults:", filteredQuotes);
        setQuotes(filteredQuotes);

        // Show success message
        const successCount = filteredQuotes.filter(
          (q) => q.variants && q.variants.length > 0 && !q.hasErrors,
        ).length;
        const errorCount = filteredQuotes.filter((q) => q.hasErrors).length;

        if (successCount > 0) {
          toast.success(
            t(
              "qtg.proposal.cascoQuotes.quotesSuccess",
              `Successfully received ${successCount} CASCO quote(s)`,
              { count: successCount },
            ),
          );
        }
        if (errorCount > 0) {
          toast.warning(
            t(
              "qtg.proposal.cascoQuotes.quotesErrors",
              `${errorCount} insurer(s) returned errors`,
              { count: errorCount },
            ),
          );
        }
      } else {
        // No CASCO data returned
        toast.warning(
          t("qtg.cascoQuotesTab.noCascoQuotesWereReturnedFromTheCalculation"),
        );
        setQuotes([]);
      }
    } catch (error: any) {
      console.error("Error fetching CASCO quotes:", error);
      toast.error(
        error?.message ||
          t("qtg.cascoQuotesTab.failedToGetCascoQuotesPleaseTryAgain"),
      );

      // Keep quotes empty on error - user can try again from selection panel
      setQuotes([]);
    } finally {
      setIsCalculating(false);
    }
  };

  // Handler for clicking invoice PDF link - refetches offer data to update payment status
  const handleInvoicePdfClick = () => {
    if (offerId) {
      // Force refetch to get updated payment status immediately
      queryClient.refetchQueries({ queryKey: [QUERY_KEYS.offer, offerId] });
    }
  };

  const handleCalculate = async () => {
    // Clear previous validation errors
    clearErrors();

    if (!canCalculateQuotes || selectedInsurers.length === 0) {
      toast.warning(
        "Please select at least one insurer and ensure all required fields are filled",
      );
      return;
    }

    if (selectedPayments.length === 0) {
      toast.warning("Please select at least one payment option");
      return;
    }

    // Get current form data
    const formData = getValues();

    // Convert selected payment IDs to payment numbers
    const selectedPaymentNumbers = selectedPayments.map((paymentId) => {
      const payment = PAYMENT_OPTIONS.find((p) => p.id === paymentId);
      return payment ? payment.payments : 1;
    });

    // Transform form data to calculation request
    const calculationRequest = transformProposalToCalculationRequest(
      formData,
      taxonomy,
      selectedInsurers.map((insurer) => insurer.toLowerCase()),
      ["casco"],
      selectedPaymentNumbers,
      userInfo,
    );

    // Validate the calculation request
    const validation = validateRequest(calculationRequest);
    if (!validation.isValid) {
      const errorCount = validation.errors.filter(
        (e) => e.severity === "error",
      ).length;
      toast.error(
        `Please correct ${errorCount} validation error${
          errorCount !== 1 ? "s" : ""
        } before calculating`,
        { autoClose: 2000 },
      );
      return;
    }

    setIsCalculating(true);

    try {
      // Call the Calculations API (does not create offer)
      await calculationMutation.mutateAsync(calculationRequest);
    } catch (error: any) {
      console.error("Error calculating CASCO quotes:", error);
      toast.error(
        error?.message ||
          t("qtg.cascoQuotesTab.failedToGetCascoQuotesPleaseTryAgain"),
      );
      setQuotes([]);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <TabWrapper>
      <ScrollableContent>
        {/* 4. Proposal Documents Section */}
        {(quotes.length > 0 ||
          savedPdfPath ||
          savedDocxPath ||
          offerPdfPath ||
          offerDocxPath ||
          invoicePdfPath ||
          invoiceDocxPath) && (
          <DocumentsSection data-help="offer-documents">
            <DocumentsTitle>
              {t("qtg.proposal.documents.title", "Proposal Documents")}
            </DocumentsTitle>
            {savedPdfPath ||
            savedDocxPath ||
            offerPdfPath ||
            offerDocxPath ||
            invoicePdfPath ||
            invoiceDocxPath ? (
              <DocumentsGrid>
                {/* Show saved PDF if available, otherwise show offer PDF */}
                {(savedPdfPath || offerPdfPath) && (
                  <FileInfoBox>
                    <FileInfoHeader>
                      <DocumentLink
                        href={savedPdfPath || offerPdfPath || "#"}
                        target="_blank"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          style={{ marginRight: "8px", flexShrink: 0 }}
                        >
                          <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
                          <path
                            d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5"
                            fill="white"
                          />
                          <text
                            x="10"
                            y="14"
                            fontSize="6"
                            fill="white"
                            textAnchor="middle"
                            fontWeight="bold"
                          >
                            PDF
                          </text>
                        </svg>
                        {savedPdfPath
                          ? t(
                              "qtg.proposal.documents.savedOfferPdf",
                              "Saved Offer (PDF)",
                            )
                          : t("qtg.proposal.documents.offerPdf", "Offer (PDF)")}
                      </DocumentLink>
                      {/* Info icon with last modified details */}
                      {offerData?.Offer?.OfferLastModifiedAt && (
                        <Tooltip
                          content={
                            <div>
                              {offerData.Offer
                                .OfferLastModifiedByUserFullName ||
                              offerData.Offer.OfferLastModifiedByUsername
                                ? t(
                                    "qtg.proposal.lifecycle.lastModified",
                                    "Last modified by {{userFullName}} ({{username}}) on {{date}}",
                                    {
                                      userFullName:
                                        offerData.Offer
                                          .OfferLastModifiedByUserFullName ||
                                        "",
                                      username:
                                        offerData.Offer
                                          .OfferLastModifiedByUsername || "",
                                      date: format(
                                        new Date(
                                          offerData.Offer.OfferLastModifiedAt,
                                        ),
                                        "dd.MM.yyyy HH:mm",
                                      ),
                                    },
                                  )
                                : t(
                                    "qtg.proposal.lifecycle.lastModifiedNoUser",
                                    "Last modified on {{date}}",
                                    {
                                      date: format(
                                        new Date(
                                          offerData.Offer.OfferLastModifiedAt,
                                        ),
                                        "dd.MM.yyyy HH:mm",
                                      ),
                                    },
                                  )}
                            </div>
                          }
                          relationship="description"
                        >
                          <Button
                            appearance="transparent"
                            size="small"
                            icon={<InfoRegular />}
                            style={{ minWidth: "auto", padding: "0 4px" }}
                          />
                        </Tooltip>
                      )}
                      {/* Hide send offer email button when invoice exists */}
                      {!invoicePdfPath && (
                        <EmailButtonContainer>
                          <Tooltip
                            content={t(
                              "qtg.proposal.buttons.sendEmail.tooltip",
                              "Send offer via email",
                            )}
                            relationship="label"
                          >
                            <Button
                              data-help="offer-send-offer-email"
                              appearance="subtle"
                              size="medium"
                              icon={<MailArrowUpRegular />}
                              onClick={() => setIsEmailDialogOpen(true)}
                              aria-label={t(
                                "qtg.proposal.documents.sendEmail",
                                "Send Email",
                              )}
                            />
                          </Tooltip>
                        </EmailButtonContainer>
                      )}
                    </FileInfoHeader>
                  </FileInfoBox>
                )}
                {/* Show saved DOCX if available, otherwise show offer DOCX */}
                {(savedDocxPath || offerDocxPath) && (
                  <FileInfoBox>
                    <DocumentLink
                      href={savedDocxPath || offerDocxPath || "#"}
                      target="_blank"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="#2B579A"
                        style={{ marginRight: "8px", flexShrink: 0 }}
                      >
                        <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
                        <path
                          d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5"
                          fill="white"
                        />
                        <text
                          x="10"
                          y="14"
                          fontSize="5"
                          fill="white"
                          textAnchor="middle"
                          fontWeight="bold"
                        >
                          DOCX
                        </text>
                      </svg>
                      {savedDocxPath
                        ? t(
                            "qtg.proposal.documents.savedOfferDocx",
                            "Saved Offer (DOCX)",
                          )
                        : t("qtg.proposal.documents.offerDocx", "Offer (DOCX)")}
                    </DocumentLink>
                  </FileInfoBox>
                )}
                {/* Invoice PDF Document */}
                {invoicePdfPath && (
                  <FileInfoBox>
                    <FileInfoHeader>
                      <DocumentLink
                        href={invoicePdfPath}
                        target="_blank"
                        onClick={handleInvoicePdfClick}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          style={{ marginRight: "8px", flexShrink: 0 }}
                        >
                          <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
                          <path
                            d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5"
                            fill="white"
                          />
                          <text
                            x="10"
                            y="14"
                            fontSize="6"
                            fill="white"
                            textAnchor="middle"
                            fontWeight="bold"
                          >
                            PDF
                          </text>
                        </svg>
                        {t(
                          "qtg.proposal.documents.invoicePdf",
                          "Invoice (PDF)",
                        )}
                      </DocumentLink>
                      <EmailButtonContainer>
                        <Tooltip
                          content={t(
                            "qtg.proposal.buttons.sendEmailInvoice.tooltip",
                            "Send invoice via email",
                          )}
                          relationship="label"
                        >
                          <Button
                            appearance="subtle"
                            size="medium"
                            icon={<MailArrowUpRegular />}
                            onClick={() => setIsInvoiceEmailDialogOpen(true)}
                            aria-label={t(
                              "qtg.proposal.documents.sendEmailInvoice",
                              "Send Invoice Email",
                            )}
                          />
                        </Tooltip>
                      </EmailButtonContainer>
                    </FileInfoHeader>
                  </FileInfoBox>
                )}
                {/* Invoice DOCX Document */}
                {invoiceDocxPath && (
                  <FileInfoBox>
                    <DocumentLink href={invoiceDocxPath} target="_blank">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="#2B579A"
                        style={{ marginRight: "8px", flexShrink: 0 }}
                      >
                        <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
                        <path
                          d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5"
                          fill="white"
                        />
                        <text
                          x="10"
                          y="14"
                          fontSize="5"
                          fill="white"
                          textAnchor="middle"
                          fontWeight="bold"
                        >
                          DOCX
                        </text>
                      </svg>
                      {t(
                        "qtg.proposal.documents.invoiceDocx",
                        "Invoice (DOCX)",
                      )}
                    </DocumentLink>
                  </FileInfoBox>
                )}
                {/* Group invoice (one document for several offers of the payer), if this offer is part of one */}
                {offerId && (
                  <GroupInvoiceDocuments
                    offerId={offerId}
                    offerData={offerData}
                    getValues={getValues}
                    userInfo={userInfo}
                    onOfferDataUpdate={onOfferDataUpdate}
                  />
                )}
              </DocumentsGrid>
            ) : (
              <NoDocumentsMessage>
                {t(
                  "qtg.proposal.documents.noDocuments",
                  "No documents available yet",
                )}
              </NoDocumentsMessage>
            )}
            {offerData?.SentAt && offerData?.SentByUserFullName && (
              <EmailSentInfo>
                {t("qtg.proposal.documents.emailSentInfo", {
                  sentByUserFullName: offerData.SentByUserFullName,
                  sentByUsername: offerData.SentByUsername || "",
                  sentTo: offerData.SentTo || "",
                  sentAt: format(
                    new Date(offerData.SentAt),
                    "dd.MM.yyyy HH:mm",
                  ),
                })}
              </EmailSentInfo>
            )}
            {offerData?.Invoice?.Casco?.SentAt &&
              offerData?.Invoice?.Casco?.SentByUserFullName && (
                <EmailSentInfo>
                  {t("qtg.proposal.documents.invoiceSentInfo", {
                    sentByUserFullName:
                      offerData.Invoice.Casco.SentByUserFullName,
                    sentByUsername:
                      offerData.Invoice.Casco.SentByUsername || "",
                    sentTo: offerData.Invoice.Casco.SentTo || "",
                    sentAt: format(
                      new Date(offerData.Invoice.Casco.SentAt),
                      "dd.MM.yyyy HH:mm",
                    ),
                  })}
                </EmailSentInfo>
              )}
          </DocumentsSection>
        )}
        {/* Insurance Data Section */}
        <InsuranceDataSection
          getError={getError}
          disabled={!!offerId || isCalculating}
          showPreviousInsuranceEndDate={false}
        />

        {/* 1. Selection Section for Insurers and Payments - Hidden after offer is created (Get Quotes) */}
        {!offerId && (
          <>
            <SelectionSection>
              {/* Insurers Panel */}
              <SelectionPanel data-help="offer-insurers-panel">
                <SelectionLabelRow>
                  <SelectionLabel>
                    <VehicleCarRegular
                      style={{
                        fontSize: "16px",
                        color: "#0078d4",
                        marginRight: "8px",
                        verticalAlign: "middle",
                      }}
                    />
                    {t(
                      "qtg.proposal.cascoQuotes.selectInsurers",
                      "Choose CASCO Insurers",
                    )}
                    <span style={{ color: "#d13438", marginLeft: "4px" }}>
                      *
                    </span>
                  </SelectionLabel>
                  <SelectionActions>
                    <Tooltip
                      content={t("qtg.proposal.selectAll", "Select All")}
                      relationship="label"
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<CheckmarkRegular />}
                        disabled={
                          createOfferMutation.isPending ||
                          isCalculating ||
                          saveOfferMutation.isPending
                        }
                        onClick={() =>
                          setValue(
                            "cascoSelections.selectedInsurers",
                            CASCO_INSURERS.filter(
                              (i) => i.id !== "Gjensidige",
                            ).map((i) => i.id),
                          )
                        }
                      />
                    </Tooltip>
                    <Tooltip
                      content={t("qtg.proposal.clearAll", "Clear All")}
                      relationship="label"
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        disabled={
                          createOfferMutation.isPending ||
                          isCalculating ||
                          saveOfferMutation.isPending
                        }
                        onClick={() =>
                          setValue("cascoSelections.selectedInsurers", [])
                        }
                      />
                    </Tooltip>
                  </SelectionActions>
                </SelectionLabelRow>
                <CheckboxList>
                  {CASCO_INSURERS.filter((a) => a.id !== "Gjensidige").map(
                    (insurer) => (
                      <div
                        key={insurer.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Checkbox
                          checked={selectedInsurers.includes(insurer.id)}
                          onChange={() => handleInsurerToggle(insurer.id)}
                          disabled={
                            createOfferMutation.isPending ||
                            isCalculating ||
                            saveOfferMutation.isPending
                          }
                        />
                        <img
                          src={`/insurers/${insurer.id.toLowerCase()}.png`}
                          alt={insurer.displayName}
                          title={insurer.displayName}
                          style={{
                            height: "20px",
                            objectFit: "contain",
                            cursor: "pointer",
                          }}
                          onClick={() => handleInsurerToggle(insurer.id)}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    ),
                  )}
                </CheckboxList>
              </SelectionPanel>

              {/* Payment Options Panel */}
              <SelectionPanel data-help="offer-payments-panel">
                <SelectionLabelRow>
                  <SelectionLabel>
                    {t(
                      "qtg.proposal.cascoQuotes.selectPayments",
                      "Choose Payment Options",
                    )}
                    <span style={{ color: "#d13438", marginLeft: "4px" }}>
                      *
                    </span>
                  </SelectionLabel>
                  <SelectionActions>
                    <Tooltip
                      content={t("qtg.proposal.selectAll", "Select All")}
                      relationship="label"
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<CheckmarkRegular />}
                        disabled={
                          createOfferMutation.isPending ||
                          isCalculating ||
                          saveOfferMutation.isPending
                        }
                        onClick={() =>
                          setValue(
                            "cascoSelections.selectedPayments",
                            PAYMENT_OPTIONS.map((p) => p.id),
                          )
                        }
                      />
                    </Tooltip>
                    <Tooltip
                      content={t("qtg.proposal.clearAll", "Clear All")}
                      relationship="label"
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        disabled={
                          createOfferMutation.isPending ||
                          isCalculating ||
                          saveOfferMutation.isPending
                        }
                        onClick={() =>
                          setValue("cascoSelections.selectedPayments", [])
                        }
                      />
                    </Tooltip>
                  </SelectionActions>
                </SelectionLabelRow>
                <CheckboxList>
                  {PAYMENT_OPTIONS.map((payment) => (
                    <Checkbox
                      key={payment.id}
                      checked={selectedPayments.includes(payment.id)}
                      onChange={() => handlePaymentToggle(payment.id)}
                      disabled={
                        createOfferMutation.isPending ||
                        isCalculating ||
                        saveOfferMutation.isPending
                      }
                      label={t(payment.labelKey)}
                    />
                  ))}
                </CheckboxList>
              </SelectionPanel>
            </SelectionSection>

            {/* Validation Summary */}
            {validationErrors.length > 0 && (
              <ValidationSummary
                errors={validationErrors}
                title="Please fix these issues before calculating CASCO quotes:"
                onErrorClick={() => scrollToFirstError()}
                showWarnings={true}
              />
            )}
          </>
        )}

        {/* 2. Quotes Display - Shown after response, above buttons */}
        {quotes.length > 0 && (
          <QuotesWrapper data-help="offer-quotes-table">
            <QuoteErrorBoundary
              fallbackTitle="CASCO Quotes Display Error"
              fallbackMessage="An error occurred while displaying CASCO quotes. The quote data may be invalid or incomplete."
            >
              <CascoQuotesDisplay
                quotes={transformedQuotes}
                cascoSaved={transformedCascoSaved}
                requestedInsurers={requestedCascoInsurers}
                requestedPayments={requestedCascoPayments}
                invoiceData={cascoInvoiceData}
                onSelectionChange={handleQuoteSelectionChange}
                updatedQuotes={cascoUpdated}
                originalPremiums={originalPremiums}
                onQuoteUpdate={handleQuoteUpdate}
                onResetDeductibles={handleResetDeductibles}
                excludedItems={cascoExcluded}
                onExclusionChange={handleExclusionChange}
                canEdit={!!offerId && !invoicePdfPath}
              />

              {/* Debug: Log updatedQuotes being passed */}
              {console.log(
                "🎨 RENDER: Passing updatedQuotes to CascoQuotesDisplay:",
                cascoUpdated,
              )}
            </QuoteErrorBoundary>
          </QuotesWrapper>
        )}
      </ScrollableContent>

      {/* Footer Panel with Action Buttons - Always visible */}
      <FooterPanel>
        {/* Additional-conditions appendix of the offer document; changing it on an existing offer enables Update Offer */}
        <Checkbox
          data-help="offer-include-additional-conditions"
          checked={includeAdditionalConditions}
          onChange={(_, data) =>
            setValue(
              "cascoSelections.includeAdditionalConditions",
              !!data.checked,
            )
          }
          disabled={
            !!invoicePdfPath ||
            isCalculating ||
            calculationMutation.isPending ||
            createOfferMutation.isPending ||
            saveOfferMutation.isPending ||
            updateOfferMutation.isPending
          }
          label={t(
            "qtg.proposal.cascoQuotes.includeAdditionalConditions",
            "Include additional conditions in the offer document",
          )}
        />
        {/* Action buttons on the right */}
        <ButtonGroup>
          {/* Preview Quotes Button - Only shown when no offerId and hideCalculateButton is false */}
          {!hideCalculateButton && (!offerId || offerId === "") && (
            <Tooltip
              content={t(
                "qtg.proposal.cascoQuotes.previewTooltip",
                "Preview quotes without creating an offer. Results will not be saved.",
              )}
              relationship="description"
            >
              <Button
                data-help="offer-preview-button"
                appearance="secondary"
                onClick={handleCalculate}
                disabled={
                  !canCalculateQuotes ||
                  selectedInsurers.length === 0 ||
                  selectedPayments.length === 0 ||
                  isCalculating ||
                  calculationMutation.isPending ||
                  createOfferMutation.isPending ||
                  saveOfferMutation.isPending ||
                  !isValid
                }
                icon={<InfoRegular />}
              >
                {calculationMutation.isPending ? (
                  <>
                    <Spinner size="tiny" style={{ marginRight: "8px" }} />{" "}
                    {t("qtg.proposal.buttons.previewing", "Previewing...")}
                  </>
                ) : (
                  t("qtg.proposal.buttons.previewQuotes", "Preview Quotes")
                )}
              </Button>
            </Tooltip>
          )}

          {/* Create Offer Button - Only shown when no offerId and no pending updates */}
          {(!offerId || offerId === "") && cascoUpdated.length === 0 && (
            <Tooltip
              content={t(
                "qtg.proposal.cascoQuotes.createOfferTooltip",
                "Create offer and generate documents. This will save the proposal to the database.",
              )}
              relationship="description"
            >
              <Button
                data-help="offer-create-offer-button"
                appearance="primary"
                onClick={handleGetQuotes}
                disabled={
                  !canCalculateQuotes ||
                  selectedInsurers.length === 0 ||
                  selectedPayments.length === 0 ||
                  isCalculating ||
                  calculationMutation.isPending ||
                  createOfferMutation.isPending ||
                  saveOfferMutation.isPending ||
                  !isValid
                }
              >
                {createOfferMutation.isPending ? (
                  <>
                    <Spinner size="tiny" style={{ marginRight: "8px" }} />{" "}
                    {t(
                      "qtg.proposal.buttons.creatingOffer",
                      "Creating Offer...",
                    )}
                  </>
                ) : (
                  t("qtg.proposal.buttons.createOffer", "Create Offer")
                )}
              </Button>
            </Tooltip>
          )}

          {/* Save Offer Button - Only shown when offerId exists and quotes exist and NO pending updates */}
          {offerId &&
            offerId !== "" &&
            quotes.length > 0 &&
            !hasNewPendingChanges && (
              <>
                {/* Create Invoice Button - Shown after offer is created and quote is selected, hidden when invoice exists */}
                {!invoicePdfPath && (
                  <Tooltip
                    content={
                      !selectedQuoteForInvoice
                        ? t(
                            "qtg.proposal.buttons.createInvoice.selectQuote",
                            "Please select a quote first",
                          )
                        : t(
                            "qtg.proposal.buttons.createInvoice.tooltip",
                            "Create invoice document for the selected quote",
                          )
                    }
                    relationship="label"
                  >
                    <Button
                      data-help="offer-create-invoice-button"
                      appearance="secondary"
                      icon={<ReceiptMoneyRegular />}
                      onClick={() => setIsInvoiceDialogOpen(true)}
                      disabled={
                        isCalculating ||
                        calculationMutation.isPending ||
                        createOfferMutation.isPending ||
                        saveOfferMutation.isPending ||
                        !selectedQuoteForInvoice
                      }
                    >
                      {t(
                        "qtg.proposal.buttons.createInvoice",
                        "Create Invoice",
                      )}
                    </Button>
                  </Tooltip>
                )}

                {/* Save Offer Button - Hidden when invoice exists */}
                {/* DISABLED: Save functionality not yet implemented for CASCO */}
                {false && !invoicePdfPath && (
                  <Tooltip
                    content={t(
                      "qtg.proposal.buttons.saveProposal.tooltip",
                      "Save the selected quotes to the offer",
                    )}
                    relationship="label"
                  >
                    <Button
                      appearance="primary"
                      onClick={handleSaveOffer}
                      disabled={
                        isCalculating ||
                        calculationMutation.isPending ||
                        createOfferMutation.isPending ||
                        saveOfferMutation.isPending ||
                        quotes.length === 0 ||
                        (offerData?.Offer?.Response?.MtplSaved &&
                          offerData.Offer.Response.MtplSaved.some(
                            (s) => s.PolicyNumber,
                          )) ||
                        (offerData?.Offer?.Response?.CascoSaved &&
                          offerData.Offer.Response.CascoSaved.some(
                            (s) => s.PolicyNumber,
                          ))
                      }
                    >
                      {saveOfferMutation.isPending ? (
                        <>
                          <Spinner size="tiny" style={{ marginRight: "8px" }} />
                          {t("qtg.proposal.buttons.saving", "Saving...")}
                        </>
                      ) : (
                        t("qtg.proposal.buttons.saveProposal", "Save Proposal")
                      )}
                    </Button>
                  </Tooltip>
                )}
              </>
            )}

          {/* Update Offer Button - Only shown when there are NEW pending quote updates */}
          {offerId && offerId !== "" && hasNewPendingChanges && (
            <Tooltip
              content={t(
                "qtg.proposal.buttons.updateOffer.tooltip",
                "Save the edited quote values to the offer",
              )}
              relationship="label"
            >
              <Button
                data-help="offer-update-button"
                appearance="primary"
                onClick={handleUpdateOffer}
                disabled={
                  isCalculating ||
                  calculationMutation.isPending ||
                  createOfferMutation.isPending ||
                  saveOfferMutation.isPending ||
                  updateOfferMutation.isPending
                }
              >
                {updateOfferMutation.isPending ? (
                  <>
                    <Spinner size="tiny" style={{ marginRight: "8px" }} />
                    {t("qtg.proposal.buttons.updating", "Updating...")}
                  </>
                ) : (
                  t("qtg.proposal.buttons.updateOffer", "Update Offer")
                )}
              </Button>
            </Tooltip>
          )}
        </ButtonGroup>
      </FooterPanel>

      {/* Send Email Dialog */}
      {offerId && (
        <SendEmailDialog
          open={isEmailDialogOpen}
          onClose={() => setIsEmailDialogOpen(false)}
          offerId={offerId}
          defaultAttachment={
            savedPdfPath || offerPdfPath
              ? {
                  name: savedPdfPath
                    ? t(
                        "qtg.proposal.documents.savedOfferPdf",
                        "Saved Offer (PDF)",
                      )
                    : t("qtg.proposal.documents.offerPdf", "Offer (PDF)"),
                  url: savedPdfPath || offerPdfPath || "",
                }
              : undefined
          }
          template={CASCO_EMAIL_TEMPLATE}
          templateData={{
            Prefix:
              getValues("clientData.clientType") === "person" ? "Cien." : "",
            ClientName:
              getValues("clientData.clientType") === "person"
                ? `${getValues("clientData.clientFirstName") || ""} ${
                    getValues("clientData.clientLastName") || ""
                  }`.trim()
                : getValues("clientData.clientName") || "",
            MarkModel: `${getValues("vehicleData.vehicleMake") || ""} ${
              getValues("vehicleData.vehicleModel") || ""
            }`.trim(),
            PlateNo: getValues("vehicleData.licensePlateNumber") || "",
            PolicyEndDate:
              formatDateTimeString(
                offerData?.Offer?.Request?.PreviousInsuranceEndDate,
              ) || "",
            ServicePersonName:
              userInfo?.userFullName || userInfo?.name || "XXX",
            ServicePersonPhone:
              userInfo?.userPhoneNumber || DEFAULT_SERVICE_PERSON_PHONE,
            ServicePersonEMail:
              userInfo?.userEmail || userInfo?.email || "xxx@greco.eu",
          }}
          defaultFromName={userInfo?.userFullName || userInfo?.name || ""}
          defaultFromEmail={userInfo?.userEmail || userInfo?.email || ""}
          defaultToEmail={getValues("clientData.clientEmail") || ""}
          onEmailSent={(updatedOffer) => {
            if (onOfferDataUpdate) {
              onOfferDataUpdate(updatedOffer);
            }
          }}
        />
      )}

      {/* Send Invoice Email Dialog */}
      {offerId && invoicePdfPath && (
        <SendEmailDialog
          open={isInvoiceEmailDialogOpen}
          onClose={() => setIsInvoiceEmailDialogOpen(false)}
          offerId={offerId}
          invoiceId={
            offerData?.Invoice?.Casco?.Response?.InvoiceId || undefined
          }
          defaultAttachment={
            invoicePdfPath
              ? {
                  name: t("qtg.proposal.documents.invoicePdf", "Invoice (PDF)"),
                  url: invoicePdfPath || "",
                }
              : undefined
          }
          template={CASCO_INVOICE_EMAIL_TEMPLATE}
          templateData={{
            Prefix: invoiceEmailAddressee.Prefix,
            ClientName: invoiceEmailAddressee.ClientName,
            MarkModel: `${getValues("vehicleData.vehicleMake") || ""} ${
              getValues("vehicleData.vehicleModel") || ""
            }`.trim(),
            PlateNo: getValues("vehicleData.licensePlateNumber") || "",
            PolicyEndDate:
              formatDateTimeString(
                offerData?.Offer?.Request?.PreviousInsuranceEndDate,
              ) || "",
            ServicePersonName:
              userInfo?.userFullName || userInfo?.name || "XXX",
            ServicePersonPhone:
              userInfo?.userPhoneNumber || DEFAULT_SERVICE_PERSON_PHONE,
            ServicePersonEMail:
              userInfo?.userEmail || userInfo?.email || "xxx@greco.eu",
          }}
          defaultFromName={userInfo?.userFullName || userInfo?.name || ""}
          defaultFromEmail={userInfo?.userEmail || userInfo?.email || ""}
          defaultToEmail={invoiceEmailAddressee.email}
          onEmailSent={(updatedOffer) => {
            if (onOfferDataUpdate) {
              onOfferDataUpdate(updatedOffer);
            }
          }}
        />
      )}

      {/* Create Invoice Dialog */}
      {offerId && (
        <CreateInvoiceDialog
          open={isInvoiceDialogOpen}
          onClose={() => setIsInvoiceDialogOpen(false)}
          offerId={offerId}
          insuranceLine="casco"
          selectedQuote={selectedQuoteForInvoice || undefined}
          isSavedOffer={!!savedPdfPath || !!savedDocxPath}
          policyNumber={undefined} // TODO: Get from policy data if available
          client={offerData?.Offer?.Request?.Client}
          onOfferUpdate={onOfferDataUpdate}
        />
      )}
    </TabWrapper>
  );
};

export default CascoQuotesTab;
